import axiosInstance from '../../services/axiosInstance';
import { coerceTrendUiText } from './errorUtils';

import type {
  RecommendedTrendSource,
  IntelligenceBriefing,
  TrendDiscoveryCandidate,
  TrendDiscoveryCoverageContract,
  TrendDiscoveryCoverageContractLane,
  TrendDiscoveryCoverageContractStatus,
  TrendDiscoveryCoverageSummary,
  TrendDiscoveryQuery,
  TrendDiscoveryRunProgress,
  TrendDiscoveryRunState,
  TrendDiscoveryRunSummary,
  TrendHardResetResult,
  TrendImageBackfillSummary,
  TrendArticle,
  TrendAssetReference,
  TrendBrandRadarHit,
  TrendCard,
  TrendModuleSettings,
  TrendPilotRunPreset,
  TrendPreviewItemResult,
  TrendPreviewRunArtifact,
  TrendOpportunity,
  TrendRunPlan,
  TrendRunRequest,
  TrendPipelineStartRequest,
  TrendPipelineRun,
  TrendRunPresentation,
  TrendResetBlockingPipelineRun,
  TrendResetBlockingState,
  TrendResetCountEntry,
  TrendResetPreview,
  TrendResetSnapshotResult,
  TrendTestSetSummary,
  TrendRecipe,
  TrendReport,
  TrendSignal,
  TrendSignalSnapshot,
  TrendStory,
  TrendSource,
  TrendSourceEvidenceRef,
  TrendSourceItem,
  TrendsOverview,
} from './types';

// Trend endpoints are served from the backend API namespace.
const BASE_URL = '/api/trends';
export type TrendQualityScope = 'all' | 'publishable';

function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return String(value);
}

function asNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asBoolean(value: unknown): boolean {
  return typeof value === 'boolean' ? value : Boolean(value);
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function normalizeTrendRunPresentation(value: any): TrendRunPresentation {
  const status = asString(value?.status);
  return {
    status: (
      status === 'latest_run_new' ||
      status === 'latest_run_updated' ||
      status === 'latest_run_reused'
    ) ? status : 'older',
    label: asString(value?.label) || 'Äldre underlag',
    latestRunId: value?.latestRunId ? asString(value.latestRunId) : null,
    latestRunStartedAt: value?.latestRunStartedAt ? asString(value.latestRunStartedAt) : null,
  };
}

function normalizeTrendAssetReference(entry: any): TrendAssetReference {
  return {
    assetId: asString(entry?.assetId),
    title: asString(entry?.title),
    description: asString(entry?.description),
    identifier: asString(entry?.identifier),
    previewUrl: asString(entry?.previewUrl),
    type: asString(entry?.type) === 'recipe' ? 'recipe' : 'product',
  };
}

function normalizeTrendArticle(article: any): TrendArticle {
  return {
    _id: asString(article?._id),
    title: asString(article?.title),
    excerpt: asString(article?.articleSections?.articleIntro || article?.excerpt),
    body: asString(article?.articleSections?.articleBody || article?.body),
    articleSections: {
      articleIntro: asString(article?.articleSections?.articleIntro || article?.excerpt),
      articleBody: asString(article?.articleSections?.articleBody || article?.body),
      bodySections: asArray<any>(article?.articleSections?.bodySections).map((entry) => ({
        heading: asString(entry?.heading),
        body: asString(entry?.body),
      })).filter((entry) => entry.heading || entry.body),
      keyPoints: asArray<unknown>(article?.articleSections?.keyPoints).map((entry) => asString(entry)).filter(Boolean),
      sourceFacts: asArray<unknown>(article?.articleSections?.sourceFacts).map((entry) => asString(entry)).filter(Boolean),
      sourceWarnings: asArray<unknown>(article?.articleSections?.sourceWarnings).map((entry) => asString(entry)).filter(Boolean),
      visualInsights: asArray<unknown>(article?.articleSections?.visualInsights).map((entry) => asString(entry)).filter(Boolean),
    },
    fontanaAnalysis: {
      whyItMatters: asString(article?.fontanaAnalysis?.whyItMatters || article?.whyItMatters),
      whatItMeansForUs: asString(article?.fontanaAnalysis?.whatItMeansForUs || article?.whatItMeansForUs),
      dvhAngle: asString(article?.fontanaAnalysis?.dvhAngle || article?.dvhAngle),
      foodserviceAngle: asString(article?.fontanaAnalysis?.foodserviceAngle || article?.foodserviceAngle),
      productConnections: asArray<unknown>(article?.fontanaAnalysis?.productConnections || article?.productConnections).map((entry) => asString(entry)).filter(Boolean),
      recipeConnections: asArray<unknown>(article?.fontanaAnalysis?.recipeConnections || article?.recipeConnections).map((entry) => asString(entry)).filter(Boolean),
      recommendedActions: asArray<unknown>(article?.fontanaAnalysis?.recommendedActions || article?.recommendedActions).map((entry) => asString(entry)).filter(Boolean),
      commercialHypothesis: asString(article?.fontanaAnalysis?.commercialHypothesis),
      firstTest: asString(article?.fontanaAnalysis?.firstTest),
      departmentOwner: asString(article?.fontanaAnalysis?.departmentOwner),
      timeHorizon: asString(article?.fontanaAnalysis?.timeHorizon),
      evidenceCaveat: asString(article?.fontanaAnalysis?.evidenceCaveat),
    },
    designAnalysis: {
      observedVisualCue: asString(article?.designAnalysis?.observedVisualCue),
      shelfImpact: asString(article?.designAnalysis?.shelfImpact),
      packagingImplication: asString(article?.designAnalysis?.packagingImplication),
      brandRisk: asString(article?.designAnalysis?.brandRisk),
      exampleToWatch: asString(article?.designAnalysis?.exampleToWatch),
    },
    heroImage: asString(article?.heroImage),
    galleryImages: asArray<unknown>(article?.galleryImages).map((entry) => asString(entry)).filter(Boolean),
    category: asString(article?.category),
    subCategory: asString(article?.subCategory),
    tags: asArray<unknown>(article?.tags).map((entry) => asString(entry)).filter(Boolean),
    regions: asArray<unknown>(article?.regions).map((entry) => asString(entry)).filter(Boolean),
    signals: asArray<unknown>(article?.signals).map((entry) => asString(entry)).filter(Boolean),
    audienceSegments: asArray<unknown>(article?.audienceSegments).map((entry) => asString(entry)).filter(Boolean),
    consumerBehaviors: asArray<unknown>(article?.consumerBehaviors).map((entry) => asString(entry)).filter(Boolean),
    usageOccasions: asArray<unknown>(article?.usageOccasions).map((entry) => asString(entry)).filter(Boolean),
    designSignals: asArray<unknown>(article?.designSignals).map((entry) => asString(entry)).filter(Boolean),
    designSubCategory: asString(article?.designSubCategory),
    sourceReferences: asArray<any>(article?.sourceReferences).map((reference) => ({
      title: asString(reference?.title),
      url: asString(reference?.url),
      note: asString(reference?.note),
      sourceName: asString(reference?.sourceName),
      publishedAt: reference?.publishedAt ? asString(reference.publishedAt) : null,
    })),
    whyItMatters: asString(article?.fontanaAnalysis?.whyItMatters || article?.whyItMatters),
    whatItMeansForUs: asString(article?.fontanaAnalysis?.whatItMeansForUs || article?.whatItMeansForUs),
    dvhAngle: asString(article?.fontanaAnalysis?.dvhAngle || article?.dvhAngle),
    foodserviceAngle: asString(article?.fontanaAnalysis?.foodserviceAngle || article?.foodserviceAngle),
    productConnections: asArray<unknown>(article?.fontanaAnalysis?.productConnections || article?.productConnections).map((entry) => asString(entry)).filter(Boolean),
    recipeConnections: asArray<unknown>(article?.fontanaAnalysis?.recipeConnections || article?.recipeConnections).map((entry) => asString(entry)).filter(Boolean),
    recommendedActions: asArray<unknown>(article?.fontanaAnalysis?.recommendedActions || article?.recommendedActions).map((entry) => asString(entry)).filter(Boolean),
    linkedProductIds: asArray<unknown>(article?.linkedProductIds).map((entry) => asString(entry)).filter(Boolean),
    linkedRecipeIds: asArray<unknown>(article?.linkedRecipeIds).map((entry) => asString(entry)).filter(Boolean),
    linkedProducts: asArray<any>(article?.linkedProducts).map(normalizeTrendAssetReference),
    linkedRecipes: asArray<any>(article?.linkedRecipes).map(normalizeTrendAssetReference),
    sourceContext: asArray<unknown>(article?.sourceContext).map((entry) => asString(entry)).filter(Boolean),
    fontanaPriorityIngredients: asArray<unknown>(article?.fontanaPriorityIngredients).map((entry) => asString(entry)).filter(Boolean),
    directActivation: asBoolean(article?.directActivation),
    editorComment: asString(article?.editorComment),
    relevanceScore: asNumber(article?.relevanceScore),
    noveltyScore: asNumber(article?.noveltyScore),
    confidenceScore: asNumber(article?.confidenceScore),
    businessImpactScore: asNumber(article?.businessImpactScore),
    designImpactScore: asNumber(article?.designImpactScore),
    provenance: asString(article?.provenance) === 'feed+discovery'
      ? 'feed+discovery'
      : asString(article?.provenance) === 'discovery'
        ? 'discovery'
        : 'feed',
    evidenceStatus: asString(article?.evidenceStatus) === 'actionable_opportunity'
      ? 'actionable_opportunity'
      : asString(article?.evidenceStatus) === 'validated_trend'
        ? 'validated_trend'
        : 'weak_signal',
    supportingSourceCount: asNumber(article?.supportingSourceCount),
    freshSourceCount: asNumber(article?.freshSourceCount),
    independentDomainCount: asNumber(article?.independentDomainCount),
    hasRecipe: asBoolean(article?.hasRecipe),
    trendRecipeId: article?.trendRecipeId ? asString(article.trendRecipeId) : null,
    featured: asBoolean(article?.featured),
    status: asString(article?.status),
    qualityStatus: asString(article?.qualityStatus) === 'blocked'
      ? 'blocked'
      : asString(article?.qualityStatus) === 'requires_review'
        ? 'requires_review'
        : 'publishable',
    reportId: article?.reportId ? asString(article.reportId) : null,
    pipelineRunId: article?.pipelineRunId ? asString(article.pipelineRunId) : null,
    publishedAt: article?.publishedAt ? asString(article.publishedAt) : null,
    createdAt: article?.createdAt ? asString(article.createdAt) : undefined,
    updatedAt: article?.updatedAt ? asString(article.updatedAt) : undefined,
    runPresentation: normalizeTrendRunPresentation(article?.runPresentation),
    publicationQuality: {
      passes: article?.publicationQuality?.passes !== false,
      reasons: asArray<unknown>(article?.publicationQuality?.reasons).map((entry) => asString(entry)).filter(Boolean),
    },
    activationQuality: {
      passes: article?.activationQuality?.passes !== false,
      reasons: asArray<unknown>(article?.activationQuality?.reasons).map((entry) => asString(entry)).filter(Boolean),
    },
    sourceItems: asArray<any>(article?.sourceItems).map((item) => ({
      _id: asString(item?._id),
      title: asString(item?.title),
      url: asString(item?.url),
      contentStatus: asString(item?.contentStatus) === 'full_article_text'
        ? 'full_article_text'
        : asString(item?.contentStatus) === 'teaser_snippet'
          ? 'teaser_snippet'
          : asString(item?.contentStatus) === 'boilerplate_dominated'
            ? 'boilerplate_dominated'
            : asString(item?.contentStatus) === 'requires_review'
              ? 'requires_review'
              : 'unknown',
      reviewStatus: asString(item?.reviewStatus) === 'required'
        ? 'required'
        : asString(item?.reviewStatus) === 'completed'
          ? 'completed'
          : 'not_required',
      qualityAssessment: {
        contentLength: asNumber(item?.qualityAssessment?.contentLength),
        longSentenceCount: asNumber(item?.qualityAssessment?.longSentenceCount),
        uniqueWordCount: asNumber(item?.qualityAssessment?.uniqueWordCount),
        issue: asString(item?.qualityAssessment?.issue),
      },
      extractionMetadata: {
        method: asString(item?.extractionMetadata?.method) === 'raw_text_only'
          ? 'raw_text_only'
          : asString(item?.extractionMetadata?.method) === 'feed_readability'
            ? 'feed_readability'
            : asString(item?.extractionMetadata?.method) === 'feed_html_strip'
              ? 'feed_html_strip'
              : asString(item?.extractionMetadata?.method) === 'http_readability'
                ? 'http_readability'
                : asString(item?.extractionMetadata?.method) === 'http_html_strip'
                  ? 'http_html_strip'
                  : asString(item?.extractionMetadata?.method) === 'headless_readability'
                    ? 'headless_readability'
                    : asString(item?.extractionMetadata?.method) === 'headless_html_strip'
                      ? 'headless_html_strip'
                      : 'unknown',
        extractedTextLength: asNumber(item?.extractionMetadata?.extractedTextLength),
        wasTruncated: item?.extractionMetadata?.wasTruncated === true,
        sectionCount: asNumber(item?.extractionMetadata?.sectionCount),
        warnings: asArray<unknown>(item?.extractionMetadata?.warnings).map((entry) => asString(entry)).filter(Boolean),
        title: asString(item?.extractionMetadata?.title),
        excerpt: asString(item?.extractionMetadata?.excerpt),
        byline: asString(item?.extractionMetadata?.byline),
        siteName: asString(item?.extractionMetadata?.siteName),
      },
    })),
  };
}

function normalizeTrendStory(story: any): TrendStory {
  return {
    _id: asString(story?._id),
    clusterKey: asString(story?.clusterKey),
    title: asString(story?.title),
    summary: asString(story?.summary),
    body: asString(story?.body),
    whatItMeansForUs: asString(story?.whatItMeansForUs),
    heroImage: asString(story?.heroImage),
    category: asString(story?.category),
    storyType: asString(story?.storyType),
    designSubCategory: asString(story?.designSubCategory),
    signalLabels: asArray<unknown>(story?.signalLabels).map((entry) => asString(entry)).filter(Boolean),
    articleIds: asArray<unknown>(story?.articleIds).map((entry) => asString(entry)).filter(Boolean),
    signalIds: asArray<unknown>(story?.signalIds).map((entry) => asString(entry)).filter(Boolean),
    opportunityIds: asArray<unknown>(story?.opportunityIds).map((entry) => asString(entry)).filter(Boolean),
    sourceReferences: asArray<any>(story?.sourceReferences).map((reference) => ({
      title: asString(reference?.title),
      url: asString(reference?.url),
      note: asString(reference?.note),
      sourceName: asString(reference?.sourceName),
      publishedAt: reference?.publishedAt ? asString(reference.publishedAt) : null,
    })),
    audienceSegments: asArray<unknown>(story?.audienceSegments).map((entry) => asString(entry)).filter(Boolean),
    consumerBehaviors: asArray<unknown>(story?.consumerBehaviors).map((entry) => asString(entry)).filter(Boolean),
    usageOccasions: asArray<unknown>(story?.usageOccasions).map((entry) => asString(entry)).filter(Boolean),
    designSignals: asArray<unknown>(story?.designSignals).map((entry) => asString(entry)).filter(Boolean),
    productConnections: asArray<unknown>(story?.productConnections).map((entry) => asString(entry)).filter(Boolean),
    recipeConnections: asArray<unknown>(story?.recipeConnections).map((entry) => asString(entry)).filter(Boolean),
    recommendedActions: asArray<unknown>(story?.recommendedActions).map((entry) => asString(entry)).filter(Boolean),
    dvhAngle: asString(story?.dvhAngle),
    foodserviceAngle: asString(story?.foodserviceAngle),
    evidenceScore: asNumber(story?.evidenceScore),
    evidenceStatus: asString(story?.evidenceStatus) === 'actionable_opportunity'
      ? 'actionable_opportunity'
      : asString(story?.evidenceStatus) === 'validated_trend'
        ? 'validated_trend'
        : 'weak_signal',
    provenance: asString(story?.provenance) === 'feed+discovery'
      ? 'feed+discovery'
      : asString(story?.provenance) === 'discovery'
        ? 'discovery'
        : 'feed',
    articleCount: asNumber(story?.articleCount),
    sourceCount: asNumber(story?.sourceCount),
    supportingSourceCount: asNumber(story?.supportingSourceCount),
    freshSourceCount: asNumber(story?.freshSourceCount),
    independentDomainCount: asNumber(story?.independentDomainCount),
    businessRelevanceScore: asNumber(story?.businessRelevanceScore),
    designRelevanceScore: asNumber(story?.designRelevanceScore),
    storyScore: asNumber(story?.storyScore),
    momentumScore: asNumber(story?.momentumScore),
    confidenceScore: asNumber(story?.confidenceScore),
    supportingArticles: asArray<any>(story?.supportingArticles).map((article) => ({
      _id: asString(article?._id),
      title: asString(article?.title),
      excerpt: asString(article?.excerpt),
      category: asString(article?.category),
      heroImage: asString(article?.heroImage),
      supportNote: asString(article?.supportNote),
      sourceName: asString(article?.sourceName),
      sourceUrl: asString(article?.sourceUrl),
      publishedAt: article?.publishedAt ? asString(article.publishedAt) : null,
      qualityStatus: asString(article?.qualityStatus) === 'blocked'
        ? 'blocked'
        : asString(article?.qualityStatus) === 'requires_review'
          ? 'requires_review'
          : 'publishable',
      publicationQuality: {
        passes: article?.publicationQuality?.passes !== false,
        reasons: asArray<unknown>(article?.publicationQuality?.reasons).map((entry) => asString(entry)).filter(Boolean),
      },
    })),
    status: asString(story?.status),
    updatedAt: story?.updatedAt ? asString(story.updatedAt) : undefined,
    publicationQuality: {
      passes: story?.publicationQuality?.passes !== false,
      reasons: asArray<unknown>(story?.publicationQuality?.reasons).map((entry) => asString(entry)).filter(Boolean),
    },
  };
}

function normalizeSourceEvidenceRef(ref: any): TrendSourceEvidenceRef {
  const sourceTier = asString(ref?.sourceTier).toUpperCase();
  const geography = asString(ref?.geography);
  const accessType = asString(ref?.accessType);
  const textQuality = asString(ref?.textQuality);
  const usageRole = asString(ref?.usageRole);
  const provenance = asString(ref?.provenance);
  return {
    sourceId: ref?.sourceId ? asString(ref.sourceId) : null,
    sourceItemId: ref?.sourceItemId ? asString(ref.sourceItemId) : null,
    articleId: ref?.articleId ? asString(ref.articleId) : null,
    discoveryCandidateId: ref?.discoveryCandidateId ? asString(ref.discoveryCandidateId) : null,
    originalUrl: asString(ref?.originalUrl),
    title: asString(ref?.title),
    sourceName: asString(ref?.sourceName),
    sourceTier: (sourceTier === 'A' || sourceTier === 'B' || sourceTier === 'C' ? sourceTier : 'D') as 'A' | 'B' | 'C' | 'D',
    geography: (
      geography === 'sweden' ||
      geography === 'nordics' ||
      geography === 'europe' ||
      geography === 'usa_uk' ||
      geography === 'global'
    ) ? geography : 'unknown',
    accessType: (
      accessType === 'fulltext' ||
      accessType === 'partial' ||
      accessType === 'snippet_only' ||
      accessType === 'rss_only' ||
      accessType === 'paywall' ||
      accessType === 'pdf_report' ||
      accessType === 'discovery'
    ) ? accessType : 'unknown',
    textQuality: (
      textQuality === 'fulltext' ||
      textQuality === 'partial' ||
      textQuality === 'snippet_only'
    ) ? textQuality : 'empty',
    usedForConclusion: ref?.usedForConclusion !== false,
    usageRole: (
      usageRole === 'primary_evidence' ||
      usageRole === 'inspiration' ||
      usageRole === 'collected_unused' ||
      usageRole === 'rejected'
    ) ? usageRole : 'supporting_evidence',
    provenance: provenance === 'discovery' || provenance === 'feed+discovery' ? provenance : 'feed',
    publishedAt: ref?.publishedAt ? asString(ref.publishedAt) : null,
    fetchedAt: ref?.fetchedAt ? asString(ref.fetchedAt) : null,
  };
}

function normalizeBrandRadarHit(hit: any): TrendBrandRadarHit {
  const sentiment = asString(hit?.sentiment);
  const classification = asString(hit?.classification);
  const status = asString(hit?.status);
  return {
    _id: asString(hit?._id),
    runId: hit?.runId ? asString(hit.runId) : null,
    query: asString(hit?.query),
    matchedTerm: asString(hit?.matchedTerm),
    title: asString(hit?.title),
    snippet: asString(hit?.snippet),
    url: asString(hit?.url),
    sourceItemId: hit?.sourceItemId ? asString(hit.sourceItemId) : null,
    discoveryCandidateId: hit?.discoveryCandidateId ? asString(hit.discoveryCandidateId) : null,
    sourceEvidenceRefs: asArray<any>(hit?.sourceEvidenceRefs).map(normalizeSourceEvidenceRef),
    sentiment: (
      sentiment === 'positive' ||
      sentiment === 'negative' ||
      sentiment === 'mixed'
    ) ? sentiment : 'neutral',
    classification: (
      classification === 'risk' ||
      classification === 'opportunity'
    ) ? classification : 'no_action',
    confidenceScore: asNumber(hit?.confidenceScore),
    severityScore: asNumber(hit?.severityScore),
    isFalsePositive: asBoolean(hit?.isFalsePositive),
    falsePositiveReason: asString(hit?.falsePositiveReason),
    reviewNote: asString(hit?.reviewNote),
    status: (
      status === 'draft' ||
      status === 'reviewed' ||
      status === 'archived'
    ) ? status : 'preview',
    publishedAt: hit?.publishedAt ? asString(hit.publishedAt) : null,
    createdAt: hit?.createdAt ? asString(hit.createdAt) : undefined,
    updatedAt: hit?.updatedAt ? asString(hit.updatedAt) : undefined,
    runPresentation: normalizeTrendRunPresentation(hit?.runPresentation),
  };
}

function normalizeTrendCard(card: any): TrendCard {
  const status = asString(card?.status);
  const qualityStatus = asString(card?.qualityStatus);
  const normalizeActionPriority = (value: unknown): 'high' | 'medium' | 'low' => {
    const priority = asString(value);
    if (priority === 'high' || priority === 'low') return priority;
    return 'medium';
  };
  return {
    _id: asString(card?._id),
    runId: card?.runId ? asString(card.runId) : null,
    title: asString(card?.title),
    shortSummary: asString(card?.shortSummary),
    detailedAnalysis: asString(card?.detailedAnalysis),
    lanes: asArray<unknown>(card?.lanes).map((entry) => asString(entry)).filter(Boolean) as TrendCard['lanes'],
    categories: asArray<unknown>(card?.categories).map((entry) => asString(entry)).filter(Boolean),
    ingredients: asArray<unknown>(card?.ingredients).map((entry) => asString(entry)).filter(Boolean),
    geography: asArray<unknown>(card?.geography).map((entry) => asString(entry)).filter(Boolean),
    evidence: {
      evidenceStrength: asNumber(card?.evidence?.evidenceStrength),
      sourceCount: asNumber(card?.evidence?.sourceCount),
      articleCount: asNumber(card?.evidence?.articleCount),
      sourceTierCounts: {
        A: asNumber(card?.evidence?.sourceTierCounts?.A),
        B: asNumber(card?.evidence?.sourceTierCounts?.B),
        C: asNumber(card?.evidence?.sourceTierCounts?.C),
        D: asNumber(card?.evidence?.sourceTierCounts?.D),
      },
      primaryGeography: (asString(card?.evidence?.primaryGeography) || 'unknown') as TrendCard['evidence']['primaryGeography'],
      hasNordicValidation: asBoolean(card?.evidence?.hasNordicValidation),
      hasPrimaryEvidence: asBoolean(card?.evidence?.hasPrimaryEvidence),
      hasOnlyInspirationSources: asBoolean(card?.evidence?.hasOnlyInspirationSources),
      newestSourceDate: card?.evidence?.newestSourceDate ? asString(card.evidence.newestSourceDate) : null,
      textQualitySummary: {
        fulltext: asNumber(card?.evidence?.textQualitySummary?.fulltext),
        partial: asNumber(card?.evidence?.textQualitySummary?.partial),
        snippet_only: asNumber(card?.evidence?.textQualitySummary?.snippet_only),
        empty: asNumber(card?.evidence?.textQualitySummary?.empty),
      },
      evidenceNotes: asArray<unknown>(card?.evidence?.evidenceNotes).map((entry) => asString(entry)).filter(Boolean),
    },
    sourceItemIds: asArray<unknown>(card?.sourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    articleIds: asArray<unknown>(card?.articleIds).map((entry) => asString(entry)).filter(Boolean),
    signalIds: asArray<unknown>(card?.signalIds).map((entry) => asString(entry)).filter(Boolean),
    sourceEvidenceRefs: asArray<any>(card?.sourceEvidenceRefs).map(normalizeSourceEvidenceRef),
    collectedButUnusedSourceItemIds: asArray<unknown>(card?.collectedButUnusedSourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    scores: {
      fontanaRelevance: asNumber(card?.scores?.fontanaRelevance),
      commercialPotential: asNumber(card?.scores?.commercialPotential),
      recipePotential: asNumber(card?.scores?.recipePotential),
      dvhPotential: asNumber(card?.scores?.dvhPotential),
      foodservicePotential: asNumber(card?.scores?.foodservicePotential),
      evidenceStrength: asNumber(card?.scores?.evidenceStrength),
      activationReadiness: asNumber(card?.scores?.activationReadiness),
      overallPriority: asNumber(card?.scores?.overallPriority),
    },
    status: (status || 'preview') as TrendCard['status'],
    qualityStatus: (qualityStatus || 'possible') as TrendCard['qualityStatus'],
    fontanaRelevance: asString(card?.fontanaRelevance),
    dvhAngle: asString(card?.dvhAngle),
    foodserviceAngle: asString(card?.foodserviceAngle),
    marketingAngle: asString(card?.marketingAngle),
    packagingAngle: asString(card?.packagingAngle),
    matchedProductIds: asArray<unknown>(card?.matchedProductIds).map((entry) => asString(entry)).filter(Boolean),
    matchedRecipeIds: asArray<unknown>(card?.matchedRecipeIds).map((entry) => asString(entry)).filter(Boolean),
    recipeGap: asString(card?.recipeGap),
    productOpportunity: asString(card?.productOpportunity),
    campaignOpportunity: asString(card?.campaignOpportunity),
    packagingOpportunity: asString(card?.packagingOpportunity),
    recommendedActions: asArray<any>(card?.recommendedActions).map((entry) => ({
      department: asString(entry?.department),
      action: asString(entry?.action),
      priority: normalizeActionPriority(entry?.priority),
    })),
    uncertainties: asArray<unknown>(card?.uncertainties).map((entry) => asString(entry)).filter(Boolean),
    validationNeeded: asArray<unknown>(card?.validationNeeded).map((entry) => asString(entry)).filter(Boolean),
    createdByModel: asString(card?.createdByModel),
    reasoningEffort: asString(card?.reasoningEffort),
    promptVersion: asString(card?.promptVersion),
    sourceKind: (asString(card?.sourceKind) || 'article') as TrendCard['sourceKind'],
    sourceKey: asString(card?.sourceKey),
    createdAt: card?.createdAt ? asString(card.createdAt) : undefined,
    updatedAt: card?.updatedAt ? asString(card.updatedAt) : undefined,
    runPresentation: normalizeTrendRunPresentation(card?.runPresentation),
  };
}

function normalizeTrendSignal(signal: any): TrendSignal {
  const status = asString(signal?.status);
  return {
    _id: asString(signal?._id),
    label: asString(signal?.label),
    normalizedLabel: asString(signal?.normalizedLabel),
    category: asString(signal?.category),
    description: asString(signal?.description),
    firstSeen: signal?.firstSeen ? asString(signal.firstSeen) : null,
    lastSeen: signal?.lastSeen ? asString(signal.lastSeen) : null,
    mentionCount: asNumber(signal?.mentionCount),
    reportCount: asNumber(signal?.reportCount),
    recurringCount: asNumber(signal?.recurringCount),
    momentumScore: asNumber(signal?.momentumScore),
    noveltyScore: asNumber(signal?.noveltyScore),
    strengthScore: asNumber(signal?.strengthScore),
    evidenceScore: asNumber(signal?.evidenceScore),
    evidenceStatus: asString(signal?.evidenceStatus) === 'actionable_opportunity'
      ? 'actionable_opportunity'
      : asString(signal?.evidenceStatus) === 'validated_trend'
        ? 'validated_trend'
        : 'weak_signal',
    provenance: asString(signal?.provenance) === 'feed+discovery'
      ? 'feed+discovery'
      : asString(signal?.provenance) === 'discovery'
        ? 'discovery'
        : 'feed',
    supportingSourceCount: asNumber(signal?.supportingSourceCount),
    freshSourceCount: asNumber(signal?.freshSourceCount),
    independentDomainCount: asNumber(signal?.independentDomainCount),
    status: (
      status === 'new' ||
      status === 'rising' ||
      status === 'declining'
    ) ? status : 'stable',
    linkedArticleIds: asArray<unknown>(signal?.linkedArticleIds).map((entry) => asString(entry)).filter(Boolean),
    relatedProductIds: asArray<unknown>(signal?.relatedProductIds).map((entry) => asString(entry)).filter(Boolean),
    relatedRecipeIds: asArray<unknown>(signal?.relatedRecipeIds).map((entry) => asString(entry)).filter(Boolean),
  };
}

function normalizeTrendOpportunity(opportunity: any): TrendOpportunity {
  const type = asString(opportunity?.type);
  const reviewStatus = asString(opportunity?.reviewStatus);
  return {
    _id: asString(opportunity?._id),
    signalId: asString(opportunity?.signalId),
    title: asString(opportunity?.title),
    type: (
      type === 'content_gap' ||
      type === 'communication_gap' ||
      type === 'portfolio_gap' ||
      type === 'underused_asset'
    ) ? type : 'strong_match',
    summary: asString(opportunity?.summary),
    whyRelevant: asString(opportunity?.whyRelevant),
    dvhAngle: asString(opportunity?.dvhAngle),
    foodserviceAngle: asString(opportunity?.foodserviceAngle),
    suggestedActions: asArray<unknown>(opportunity?.suggestedActions).map((entry) => asString(entry)).filter(Boolean),
    linkedProductIds: asArray<unknown>(opportunity?.linkedProductIds).map((entry) => asString(entry)).filter(Boolean),
    linkedRecipeIds: asArray<unknown>(opportunity?.linkedRecipeIds).map((entry) => asString(entry)).filter(Boolean),
    linkedProducts: asArray<any>(opportunity?.linkedProducts).map(normalizeTrendAssetReference),
    linkedRecipes: asArray<any>(opportunity?.linkedRecipes).map(normalizeTrendAssetReference),
    linkedArticles: asArray<any>(opportunity?.linkedArticles).map((article: any) => ({
      _id: asString(article?._id),
      title: asString(article?.title),
      category: asString(article?.category),
      heroImage: asString(article?.heroImage),
      qualityStatus: asString(article?.qualityStatus) === 'blocked'
        ? 'blocked'
        : asString(article?.qualityStatus) === 'requires_review'
          ? 'requires_review'
          : 'publishable',
    })),
    sourceEvidenceRefs: asArray<any>(opportunity?.sourceEvidenceRefs).map(normalizeSourceEvidenceRef),
    collectedButUnusedSourceItemIds: asArray<unknown>(opportunity?.collectedButUnusedSourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    sourceReferences: asArray<any>(opportunity?.sourceReferences).map((reference) => ({
      title: asString(reference?.title),
      url: asString(reference?.url),
      note: asString(reference?.note),
      sourceName: asString(reference?.sourceName),
      publishedAt: reference?.publishedAt ? asString(reference.publishedAt) : null,
    })),
    freshestSourcePublishedAt: opportunity?.freshestSourcePublishedAt ? asString(opportunity.freshestSourcePublishedAt) : null,
    oldestSourcePublishedAt: opportunity?.oldestSourcePublishedAt ? asString(opportunity.oldestSourcePublishedAt) : null,
    confidenceScore: asNumber(opportunity?.confidenceScore),
    evidenceScore: asNumber(opportunity?.evidenceScore),
    evidenceStatus: asString(opportunity?.evidenceStatus) === 'actionable_opportunity'
      ? 'actionable_opportunity'
      : asString(opportunity?.evidenceStatus) === 'validated_trend'
        ? 'validated_trend'
        : 'weak_signal',
    provenance: asString(opportunity?.provenance) === 'feed+discovery'
      ? 'feed+discovery'
      : asString(opportunity?.provenance) === 'discovery'
        ? 'discovery'
        : 'feed',
    reviewStatus: (
      reviewStatus === 'review' ||
      reviewStatus === 'approved'
    ) ? reviewStatus : 'draft',
    updatedAt: opportunity?.updatedAt ? asString(opportunity.updatedAt) : undefined,
    runPresentation: normalizeTrendRunPresentation(opportunity?.runPresentation),
    publicationQuality: {
      passes: opportunity?.publicationQuality?.passes !== false,
      reasons: asArray<unknown>(opportunity?.publicationQuality?.reasons).map((entry) => asString(entry)).filter(Boolean),
    },
  };
}

function normalizeTrendReport(report: any): TrendReport {
  const categoryRunMode = asString(report?.categoryRunMode) === 'design_packaging'
    ? 'design_packaging'
    : asString(report?.categoryRunMode) === 'food_recipe'
      ? 'food_recipe'
      : asString(report?.categoryRunMode) === 'market_competition'
        ? 'market_competition'
        : 'full';
  const status = asString(report?.status);
  return {
    _id: asString(report?._id),
    title: asString(report?.title),
    slug: asString(report?.slug),
    pipelineRunId: report?.pipelineRunId ? asString(report.pipelineRunId) : null,
    categoryRunMode,
    categoryAllowlist: asArray<unknown>(report?.categoryAllowlist)
      .map((entry) => asString(entry))
      .filter((entry): entry is TrendReport['categoryAllowlist'][number] => (
        entry === 'design'
        || entry === 'packaging'
        || entry === 'food'
        || entry === 'consumer'
        || entry === 'market'
        || entry === 'competition'
      )),
    issueDate: asString(report?.issueDate),
    periodStart: asString(report?.periodStart),
    periodEnd: asString(report?.periodEnd),
    intro: asString(report?.intro),
    editorSummary: asString(report?.editorSummary),
    whatItMeansForUs: asString(report?.whatItMeansForUs),
    heroArticleId: report?.heroArticleId ? asString(report.heroArticleId) : null,
    heroStoryId: report?.heroStoryId ? asString(report.heroStoryId) : null,
    articleIds: asArray<unknown>(report?.articleIds).map((entry) => asString(entry)).filter(Boolean),
    storyIds: asArray<unknown>(report?.storyIds).map((entry) => asString(entry)).filter(Boolean),
    opportunityIds: asArray<unknown>(report?.opportunityIds).map((entry) => asString(entry)).filter(Boolean),
    sectionOrder: asArray<unknown>(report?.sectionOrder).map((entry) => asString(entry)).filter(Boolean),
    overviewManualMode: report?.overviewManualMode === true,
    highlights: asArray<unknown>(report?.highlights).map((entry) => asString(entry)).filter(Boolean),
    pinnedWeeklyArticleIds: asArray<unknown>(report?.pinnedWeeklyArticleIds).map((entry) => asString(entry)).filter(Boolean),
    pinnedTopStoryIds: asArray<unknown>(report?.pinnedTopStoryIds).map((entry) => asString(entry)).filter(Boolean),
    pinnedLatestArticleIds: asArray<unknown>(report?.pinnedLatestArticleIds).map((entry) => asString(entry)).filter(Boolean),
    pinnedOpportunityIds: asArray<unknown>(report?.pinnedOpportunityIds).map((entry) => asString(entry)).filter(Boolean),
    hiddenArticleIds: asArray<unknown>(report?.hiddenArticleIds).map((entry) => asString(entry)).filter(Boolean),
    status: (
      status === 'review' ||
      status === 'published'
    ) ? status : 'draft',
    publishedAt: report?.publishedAt ? asString(report.publishedAt) : null,
    updatedAt: report?.updatedAt ? asString(report.updatedAt) : undefined,
  };
}

function normalizeBriefingPriority(value: unknown): 'low' | 'medium' | 'high' {
  const normalized = asString(value);
  if (normalized === 'high' || normalized === 'low') return normalized;
  return 'medium';
}

function normalizeIntelligenceBriefing(briefing: any): IntelligenceBriefing {
  return {
    _id: asString(briefing?._id),
    runId: briefing?.runId ? asString(briefing.runId) : null,
    reportId: briefing?.reportId ? asString(briefing.reportId) : null,
    title: asString(briefing?.title),
    issueDate: asString(briefing?.issueDate),
    status: (asString(briefing?.status) || 'draft') as IntelligenceBriefing['status'],
    executiveSummary: asString(briefing?.executiveSummary),
    mainConclusion: asString(briefing?.mainConclusion),
    whatChanged: asArray<unknown>(briefing?.whatChanged).map((entry) => asString(entry)).filter(Boolean),
    topTrendCardIds: asArray<unknown>(briefing?.topTrendCardIds).map((entry) => asString(entry)).filter(Boolean),
    topOpportunityIds: asArray<unknown>(briefing?.topOpportunityIds).map((entry) => asString(entry)).filter(Boolean),
    departmentBriefs: asArray<any>(briefing?.departmentBriefs).map((entry) => ({
      department: (asString(entry?.department) || 'leadership') as IntelligenceBriefing['departmentBriefs'][number]['department'],
      label: asString(entry?.label || entry?.department),
      summary: asString(entry?.summary),
      relevantTrendCardIds: asArray<unknown>(entry?.relevantTrendCardIds).map((item) => asString(item)).filter(Boolean),
      relevantOpportunityIds: asArray<unknown>(entry?.relevantOpportunityIds).map((item) => asString(item)).filter(Boolean),
      recommendedActions: asArray<any>(entry?.recommendedActions).map((action) => ({
        action: asString(action?.action),
        priority: normalizeBriefingPriority(action?.priority),
        source: asString(action?.source),
      })).filter((action) => action.action),
      sourceEvidenceRefs: asArray<any>(entry?.sourceEvidenceRefs).map(normalizeSourceEvidenceRef),
      uncertainties: asArray<unknown>(entry?.uncertainties).map((item) => asString(item)).filter(Boolean),
      validationNeeded: asArray<unknown>(entry?.validationNeeded).map((item) => asString(item)).filter(Boolean),
    })),
    sourceSummary: {
      sourceCount: asNumber(briefing?.sourceSummary?.sourceCount),
      primarySourceCount: asNumber(briefing?.sourceSummary?.primarySourceCount),
      nordicSourceCount: asNumber(briefing?.sourceSummary?.nordicSourceCount),
      inspirationOnlyCount: asNumber(briefing?.sourceSummary?.inspirationOnlyCount),
      evidenceWarnings: asArray<unknown>(briefing?.sourceSummary?.evidenceWarnings).map((entry) => asString(entry)).filter(Boolean),
    },
    risksAndUncertainties: asArray<unknown>(briefing?.risksAndUncertainties).map((entry) => asString(entry)).filter(Boolean),
    sourceEvidenceRefs: asArray<any>(briefing?.sourceEvidenceRefs).map(normalizeSourceEvidenceRef),
    collectedButUnusedSourceItemIds: asArray<unknown>(briefing?.collectedButUnusedSourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    recommendedNextActions: asArray<any>(briefing?.recommendedNextActions).map((entry) => ({
      department: asString(entry?.department),
      action: asString(entry?.action),
      priority: normalizeBriefingPriority(entry?.priority),
    })).filter((entry) => entry.action),
    createdByModel: asString(briefing?.createdByModel),
    reasoningEffort: asString(briefing?.reasoningEffort),
    promptVersion: asString(briefing?.promptVersion),
    createdAt: briefing?.createdAt ? asString(briefing.createdAt) : undefined,
    updatedAt: briefing?.updatedAt ? asString(briefing.updatedAt) : undefined,
  };
}

function normalizeTrendRunMode(value: unknown): TrendRunPlan['runMode'] {
  const normalized = asString(value);
  if (
    normalized === 'source_test'
    || normalized === 'prompt_test'
    || normalized === 'model_test'
    || normalized === 'brand_radar'
    || normalized === 'category_preview'
    || normalized === 'standard'
    || normalized === 'premium'
    || normalized === 'report_only'
    || normalized === 'second_opinion_only'
  ) {
    return normalized;
  }
  return 'micro_test';
}

function normalizeTrendPublishMode(value: unknown): TrendRunPlan['publishMode'] {
  const normalized = asString(value);
  if (normalized === 'draft' || normalized === 'published') return normalized;
  return 'preview';
}

function normalizeTrendAiProfileKey(value: unknown): TrendRunPlan['aiProfile'] {
  const normalized = asString(value);
  if (normalized === 'standard' || normalized === 'premium' || normalized === 'report_only' || normalized === 'model_test') {
    return normalized;
  }
  return 'preview';
}

function normalizeTrendLane(value: unknown): TrendRunPlan['scope']['lanes'][number] | null {
  const normalized = asString(value);
  if (
    normalized === 'recipes'
    || normalized === 'social'
    || normalized === 'design'
    || normalized === 'packaging'
    || normalized === 'dvh'
    || normalized === 'foodservice'
    || normalized === 'market'
    || normalized === 'brand_radar'
    || normalized === 'consumer'
    || normalized === 'regulation'
  ) {
    return normalized;
  }
  return null;
}

function normalizeTrendRunScope(scope: any): TrendRunPlan['scope'] {
  return {
    lanes: asArray<unknown>(scope?.lanes).map(normalizeTrendLane).filter((entry): entry is TrendRunPlan['scope']['lanes'][number] => !!entry),
    sourceIds: asArray<unknown>(scope?.sourceIds).map((entry) => asString(entry)).filter(Boolean),
    sourceItemIds: asArray<unknown>(scope?.sourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    discoveryQueryIds: asArray<unknown>(scope?.discoveryQueryIds).map((entry) => asString(entry)).filter(Boolean),
    brandRadarQueries: asArray<unknown>(scope?.brandRadarQueries).map((entry) => asString(entry)).filter(Boolean),
    channels: asArray<unknown>(scope?.channels).map((entry) => asString(entry)).filter(Boolean),
  };
}

function normalizeTrendRunPhases(phases: any): TrendRunPlan['phases'] {
  return {
    fetchSources: phases?.fetchSources !== false,
    discovery: phases?.discovery !== false,
    normalizeAndDedupe: phases?.normalizeAndDedupe !== false,
    researchSynthesis: phases?.researchSynthesis !== false,
    analyzeArticles: phases?.analyzeArticles !== false,
    aggregateSignals: phases?.aggregateSignals !== false,
    verifySignals: phases?.verifySignals !== false,
    matchProducts: phases?.matchProducts !== false,
    matchRecipes: phases?.matchRecipes !== false,
    computeOpportunities: phases?.computeOpportunities !== false,
    generateTrendCards: phases?.generateTrendCards !== false,
    generateRecipes: phases?.generateRecipes !== false,
    generateBriefing: phases?.generateBriefing === true,
    runSecondOpinion: phases?.runSecondOpinion === true,
  };
}

function normalizeTrendRunLimits(limits: any): TrendRunPlan['limits'] {
  return {
    maxSources: asNumber(limits?.maxSources),
    maxItemsPerSource: asNumber(limits?.maxItemsPerSource),
    maxArticlesToAnalyze: asNumber(limits?.maxArticlesToAnalyze),
    maxSignalsToVerify: asNumber(limits?.maxSignalsToVerify),
    maxEstimatedCost: asNumber(limits?.maxEstimatedCost),
  };
}

function normalizeTrendRunPlan(plan: any): TrendRunPlan {
  const categoryRunMode = asString(plan?.categoryRunMode) === 'design_packaging'
    ? 'design_packaging'
    : asString(plan?.categoryRunMode) === 'food_recipe'
      ? 'food_recipe'
      : asString(plan?.categoryRunMode) === 'market_competition'
        ? 'market_competition'
        : 'full';
  return {
    runMode: normalizeTrendRunMode(plan?.runMode),
    publishMode: normalizeTrendPublishMode(plan?.publishMode),
    aiProfile: normalizeTrendAiProfileKey(plan?.aiProfile),
    scope: normalizeTrendRunScope(plan?.scope),
    phases: normalizeTrendRunPhases(plan?.phases),
    limits: normalizeTrendRunLimits(plan?.limits),
    categoryRunMode,
    categoryAllowlist: asArray<unknown>(plan?.categoryAllowlist)
      .map((entry) => asString(entry))
      .filter((entry): entry is TrendRunPlan['categoryAllowlist'][number] => (
        entry === 'design'
        || entry === 'packaging'
        || entry === 'food'
        || entry === 'consumer'
        || entry === 'market'
        || entry === 'competition'
      )),
    startOptions: plan?.startOptions || {},
    estimatedCost: {
      currency: 'USD',
      min: asNumber(plan?.estimatedCost?.min),
      max: asNumber(plan?.estimatedCost?.max),
      note: asString(plan?.estimatedCost?.note),
    },
    warnings: asArray<unknown>(plan?.warnings).map((entry) => asString(entry)).filter(Boolean),
    canStart: plan?.canStart !== false,
  };
}

function normalizeTrendPipelineRun(run: any): TrendPipelineRun {
  const categoryRunMode = asString(run?.categoryRunMode) === 'design_packaging'
    ? 'design_packaging'
    : asString(run?.categoryRunMode) === 'food_recipe'
      ? 'food_recipe'
      : asString(run?.categoryRunMode) === 'market_competition'
        ? 'market_competition'
        : 'full';
  return {
    _id: asString(run?._id),
    trigger: asString(run?.trigger) === 'scheduled'
      ? 'scheduled'
      : asString(run?.trigger) === 'report_generate'
        ? 'report_generate'
        : 'manual',
    scope: asString(run?.scope) === 'preview' ? 'preview' : 'live',
    profile: asString(run?.profile) === 'mini'
      ? 'mini'
      : asString(run?.profile) === 'micro'
        ? 'micro'
        : 'full',
    inputMode: asString(run?.inputMode) === 'fixture'
      ? 'fixture'
      : asString(run?.inputMode) === 'source_items'
        ? 'source_items'
        : 'live_sources',
    runMode: normalizeTrendRunMode(run?.runMode),
    publishMode: normalizeTrendPublishMode(run?.publishMode),
    aiProfile: normalizeTrendAiProfileKey(run?.aiProfile),
    categoryRunMode,
    categoryAllowlist: asArray<unknown>(run?.categoryAllowlist)
      .map((entry) => asString(entry))
      .filter((entry): entry is TrendPipelineRun['categoryAllowlist'][number] => (
        entry === 'design'
        || entry === 'packaging'
        || entry === 'food'
        || entry === 'consumer'
        || entry === 'market'
        || entry === 'competition'
      )),
    plannedScope: run?.plannedScope ? normalizeTrendRunScope(run.plannedScope) : null,
    plannedPhases: run?.plannedPhases ? normalizeTrendRunPhases(run.plannedPhases) : null,
    plannedLimits: run?.plannedLimits ? normalizeTrendRunLimits(run.plannedLimits) : null,
    fixtureKey: run?.fixtureKey ? asString(run.fixtureKey) : null,
    requestedSourceIds: asArray<unknown>(run?.requestedSourceIds).map((entry) => asString(entry)).filter(Boolean),
    requestedSourceItemIds: asArray<unknown>(run?.requestedSourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    status: asString(run?.status),
    startedAt: asString(run?.startedAt),
    heartbeatAt: run?.heartbeatAt ? asString(run.heartbeatAt) : null,
    phaseStartedAt: run?.phaseStartedAt ? asString(run.phaseStartedAt) : null,
    finishedAt: run?.finishedAt ? asString(run.finishedAt) : null,
    lastStep: asString(run?.lastStep),
    reportId: run?.reportId ? asString(run.reportId) : null,
    counters: {
      fetched: asNumber(run?.counters?.fetched),
      normalized: asNumber(run?.counters?.normalized),
      analyzed: asNumber(run?.counters?.analyzed),
      signals: asNumber(run?.counters?.signals),
      opportunities: asNumber(run?.counters?.opportunities),
    },
    discoveryCoverage: run?.discoveryCoverage ? normalizeTrendDiscoveryCoverageSummary(run?.discoveryCoverage) : null,
    models: {
      deepResearchModel: asString(run?.models?.deepResearchModel),
      analysisModel: asString(run?.models?.analysisModel),
      verificationModel: asString(run?.models?.verificationModel),
    },
    progress: run?.progress
      ? {
        phase: asString(run.progress?.phase),
        message: asString(run.progress?.message),
        current: run?.progress?.current === null || run?.progress?.current === undefined
          ? null
          : asNumber(run.progress.current),
        total: run?.progress?.total === null || run?.progress?.total === undefined
          ? null
          : asNumber(run.progress.total),
      }
      : null,
    resultSummary: run?.resultSummary
      ? {
        headline: asString(run.resultSummary?.headline),
        testedItems: run?.resultSummary?.testedItems === null || run?.resultSummary?.testedItems === undefined
          ? null
          : asNumber(run.resultSummary.testedItems),
        publishableItems: run?.resultSummary?.publishableItems === null || run?.resultSummary?.publishableItems === undefined
          ? null
          : asNumber(run.resultSummary.publishableItems),
        blockedItems: run?.resultSummary?.blockedItems === null || run?.resultSummary?.blockedItems === undefined
          ? null
          : asNumber(run.resultSummary.blockedItems),
        ignoredItems: run?.resultSummary?.ignoredItems === null || run?.resultSummary?.ignoredItems === undefined
          ? null
          : asNumber(run.resultSummary.ignoredItems),
        reviewItems: run?.resultSummary?.reviewItems === null || run?.resultSummary?.reviewItems === undefined
          ? null
          : asNumber(run.resultSummary.reviewItems),
      }
      : null,
    notes: asArray<unknown>(run?.notes).map((entry) => asString(entry)).filter(Boolean),
    error: asString(run?.error),
    updatedAt: run?.updatedAt ? asString(run.updatedAt) : undefined,
  };
}

function normalizeTrendResetCountEntry(entry: any): TrendResetCountEntry {
  return {
    key: asString(entry?.key),
    label: asString(entry?.label),
    count: asNumber(entry?.count),
  };
}

function normalizeTrendResetBlockingPipelineRun(run: any): TrendResetBlockingPipelineRun | null {
  if (!run?._id) return null;
  return {
    _id: asString(run?._id),
    status: asString(run?.status),
    lastStep: asString(run?.lastStep),
    startedAt: run?.startedAt ? asString(run.startedAt) : null,
    updatedAt: run?.updatedAt ? asString(run.updatedAt) : null,
    heartbeatAt: run?.heartbeatAt ? asString(run.heartbeatAt) : null,
  };
}

function normalizeTrendResetBlockingState(state: any): TrendResetBlockingState {
  return {
    pipelineRunningInMemory: state?.pipelineRunningInMemory === true,
    activePipelineRun: normalizeTrendResetBlockingPipelineRun(state?.activePipelineRun),
    discoveryRun: state?.discoveryRun ? normalizeTrendDiscoveryRunState(state.discoveryRun) : null,
    blockingReasons: asArray<unknown>(state?.blockingReasons).map((entry) => asString(entry)).filter(Boolean),
    canHardReset: state?.canHardReset === true,
  };
}

function normalizeTrendResetPreview(preview: any): TrendResetPreview {
  return {
    requiredConfirmText: asString(preview?.requiredConfirmText),
    deletableCollections: asArray<any>(preview?.deletableCollections).map(normalizeTrendResetCountEntry),
    retainedCollections: asArray<any>(preview?.retainedCollections).map(normalizeTrendResetCountEntry),
    artifactFiles: asArray<any>(preview?.artifactFiles).map(normalizeTrendResetCountEntry),
    totalDocumentsToDelete: asNumber(preview?.totalDocumentsToDelete),
    totalFilesToDelete: asNumber(preview?.totalFilesToDelete),
    blockingState: normalizeTrendResetBlockingState(preview?.blockingState),
  };
}

function normalizeTrendResetSnapshotResult(result: any): TrendResetSnapshotResult {
  return {
    snapshotId: asString(result?.snapshotId),
    fileName: asString(result?.fileName),
    createdAt: asString(result?.createdAt),
    sizeBytes: asNumber(result?.sizeBytes),
    downloadUrl: asString(result?.downloadUrl),
    preview: normalizeTrendResetPreview(result?.preview),
  };
}

function normalizeTrendHardResetResult(result: any): TrendHardResetResult {
  return {
    snapshotId: asString(result?.snapshotId),
    snapshotFileName: asString(result?.snapshotFileName),
    snapshotDownloadUrl: asString(result?.snapshotDownloadUrl),
    resetAt: asString(result?.resetAt),
    clearedCollections: asArray<any>(result?.clearedCollections).map(normalizeTrendResetCountEntry),
    clearedArtifactFiles: asArray<any>(result?.clearedArtifactFiles).map(normalizeTrendResetCountEntry),
    totalDocumentsDeleted: asNumber(result?.totalDocumentsDeleted),
    totalFilesDeleted: asNumber(result?.totalFilesDeleted),
    restartedRun: result?.restartedRun ? normalizeTrendPipelineRun(result.restartedRun) : null,
    restartError: asString(result?.restartError),
  };
}

function normalizeTrendPreviewItemResult(item: any): TrendPreviewItemResult {
  return {
    inputKey: asString(item?.inputKey),
    sourceId: asString(item?.sourceId),
    title: asString(item?.title),
    category: asString(item?.category),
    sourceName: asString(item?.sourceName),
    url: asString(item?.url),
    publishedAt: item?.publishedAt ? asString(item.publishedAt) : null,
    status: asString(item?.status) === 'publishable'
      ? 'publishable'
      : asString(item?.status) === 'requires_review'
        ? 'requires_review'
      : asString(item?.status) === 'ignored'
        ? 'ignored'
        : 'blocked',
    reasons: asArray<unknown>(item?.reasons).map((entry) => asString(entry)).filter(Boolean),
    textQuality: {
      isUsable: asBoolean(item?.textQuality?.isUsable),
      isThin: asBoolean(item?.textQuality?.isThin),
      isBoilerplateHeavy: asBoolean(item?.textQuality?.isBoilerplateHeavy),
      requiresReview: asBoolean(item?.textQuality?.requiresReview),
      contentStatus: asString(item?.textQuality?.contentStatus) === 'full_article_text'
        ? 'full_article_text'
        : asString(item?.textQuality?.contentStatus) === 'teaser_snippet'
          ? 'teaser_snippet'
          : asString(item?.textQuality?.contentStatus) === 'boilerplate_dominated'
            ? 'boilerplate_dominated'
            : asString(item?.textQuality?.contentStatus) === 'requires_review'
              ? 'requires_review'
              : 'unknown',
      reviewStatus: asString(item?.textQuality?.reviewStatus) === 'required'
        ? 'required'
        : asString(item?.textQuality?.reviewStatus) === 'completed'
          ? 'completed'
          : 'not_required',
      contentLength: asNumber(item?.textQuality?.contentLength),
      longSentenceCount: asNumber(item?.textQuality?.longSentenceCount),
      uniqueWordCount: asNumber(item?.textQuality?.uniqueWordCount),
      issue: asString(item?.textQuality?.issue),
    },
    freshness: {
      isFresh: asBoolean(item?.freshness?.isFresh),
      reason: asString(item?.freshness?.reason),
    },
    analysis: {
      usedFallback: asBoolean(item?.analysis?.usedFallback),
      aiAvailable: asBoolean(item?.analysis?.aiAvailable),
    },
    article: item?.article
      ? {
        title: asString(item.article?.title),
        excerpt: asString(item.article?.excerpt),
        bodyPreview: asString(item.article?.bodyPreview),
        heroImage: asString(item.article?.heroImage),
        galleryImageCount: asNumber(item.article?.galleryImageCount),
        keyPoints: asArray<unknown>(item.article?.keyPoints).map((entry) => asString(entry)).filter(Boolean),
        sourceFacts: asArray<unknown>(item.article?.sourceFacts).map((entry) => asString(entry)).filter(Boolean),
        sourceWarnings: asArray<unknown>(item.article?.sourceWarnings).map((entry) => asString(entry)).filter(Boolean),
        visualInsights: asArray<unknown>(item.article?.visualInsights).map((entry) => asString(entry)).filter(Boolean),
        publicationQuality: {
          passes: item?.article?.publicationQuality?.passes !== false,
          reasons: asArray<unknown>(item?.article?.publicationQuality?.reasons).map((entry) => asString(entry)).filter(Boolean),
        },
        qualityStatus: asString(item?.article?.qualityStatus) === 'blocked'
          ? 'blocked'
          : asString(item?.article?.qualityStatus) === 'requires_review'
            ? 'requires_review'
            : 'publishable',
      }
      : null,
  };
}

function normalizeTrendPreviewRunArtifact(artifact: any): TrendPreviewRunArtifact {
  return {
    runId: asString(artifact?.runId),
    profile: asString(artifact?.profile) === 'mini'
      ? 'mini'
      : asString(artifact?.profile) === 'micro'
        ? 'micro'
        : 'full',
    inputMode: asString(artifact?.inputMode) === 'fixture'
      ? 'fixture'
      : asString(artifact?.inputMode) === 'source_items'
        ? 'source_items'
        : 'live_sources',
    fixtureKey: artifact?.fixtureKey ? asString(artifact.fixtureKey) : null,
    requestedSourceIds: asArray<unknown>(artifact?.requestedSourceIds).map((entry) => asString(entry)).filter(Boolean),
    requestedSourceItemIds: asArray<unknown>(artifact?.requestedSourceItemIds).map((entry) => asString(entry)).filter(Boolean),
    generatedAt: asString(artifact?.generatedAt || artifact?.createdAt),
    counts: {
      testedItems: asNumber(artifact?.counts?.testedItems),
      analyzedItems: asNumber(artifact?.counts?.analyzedItems),
      publishableItems: asNumber(artifact?.counts?.publishableItems),
      blockedItems: asNumber(artifact?.counts?.blockedItems),
      ignoredItems: asNumber(artifact?.counts?.ignoredItems),
      reviewItems: asNumber(artifact?.counts?.reviewItems),
      heroImageHits: asNumber(artifact?.counts?.heroImageHits),
      fallbackItems: asNumber(artifact?.counts?.fallbackItems),
    },
    topBlockingReasons: asArray<any>(artifact?.topBlockingReasons).map((entry) => ({
      reason: asString(entry?.reason),
      count: asNumber(entry?.count),
    })).filter((entry) => entry.reason),
    samples: {
      publishable: asArray<any>(artifact?.samples?.publishable).map(normalizeTrendPreviewItemResult),
      blocked: asArray<any>(artifact?.samples?.blocked).map(normalizeTrendPreviewItemResult),
      ignored: asArray<any>(artifact?.samples?.ignored).map(normalizeTrendPreviewItemResult),
    },
    items: asArray<any>(artifact?.items).map(normalizeTrendPreviewItemResult),
    notes: asArray<unknown>(artifact?.notes).map((entry) => asString(entry)).filter(Boolean),
    summary: artifact?.summary
      ? {
        headline: asString(artifact.summary?.headline),
        testedItems: artifact?.summary?.testedItems === null || artifact?.summary?.testedItems === undefined
          ? null
          : asNumber(artifact.summary.testedItems),
        publishableItems: artifact?.summary?.publishableItems === null || artifact?.summary?.publishableItems === undefined
          ? null
          : asNumber(artifact.summary.publishableItems),
        blockedItems: artifact?.summary?.blockedItems === null || artifact?.summary?.blockedItems === undefined
          ? null
          : asNumber(artifact.summary.blockedItems),
        ignoredItems: artifact?.summary?.ignoredItems === null || artifact?.summary?.ignoredItems === undefined
          ? null
          : asNumber(artifact.summary.ignoredItems),
        reviewItems: artifact?.summary?.reviewItems === null || artifact?.summary?.reviewItems === undefined
          ? null
          : asNumber(artifact.summary.reviewItems),
      }
      : {
        headline: '',
        testedItems: null,
        publishableItems: null,
        blockedItems: null,
        ignoredItems: null,
        reviewItems: null,
      },
  };
}

function normalizeTrendSource(source: any): TrendSource {
  return {
    _id: asString(source?._id),
    name: asString(source?.name),
    type: asString(source?.type) === 'rss'
      ? 'rss'
      : asString(source?.type) === 'api'
        ? 'api'
        : asString(source?.type) === 'manual'
          ? 'manual'
          : 'website',
    url: asString(source?.url),
    category: asString(source?.category),
    language: asString(source?.language),
    focusTags: asArray<unknown>(source?.focusTags).map((entry) => asString(entry)).filter(Boolean),
    notes: asString(source?.notes),
    active: asBoolean(source?.active),
    weight: asNumber(source?.weight),
    sourceTier: ['A', 'B', 'C', 'D'].includes(asString(source?.sourceTier)) ? asString(source.sourceTier) as TrendSource['sourceTier'] : 'D',
    sourceLayer: asString(source?.sourceLayer) === 'primary_nordic'
      ? 'primary_nordic'
      : asString(source?.sourceLayer) === 'secondary_europe'
        ? 'secondary_europe'
        : asString(source?.sourceLayer) === 'inspiration_global'
          ? 'inspiration_global'
          : 'weak_support',
    geography: asString(source?.geography) === 'sweden'
      ? 'sweden'
      : asString(source?.geography) === 'nordics'
        ? 'nordics'
        : asString(source?.geography) === 'europe'
          ? 'europe'
          : asString(source?.geography) === 'usa_uk'
            ? 'usa_uk'
            : asString(source?.geography) === 'global'
              ? 'global'
              : 'unknown',
    sourceType: asString(source?.sourceType) as TrendSource['sourceType'] || 'other',
    accessType: asString(source?.accessType) as TrendSource['accessType'] || 'unknown',
    evidenceWeight: asNumber(source?.evidenceWeight),
    recommendedUpdateFrequency: asString(source?.recommendedUpdateFrequency) as TrendSource['recommendedUpdateFrequency'] || 'weekly',
    sourceHealthScore: source?.sourceHealthScore === null || source?.sourceHealthScore === undefined ? null : asNumber(source.sourceHealthScore),
    lastSuccessfulFetchAt: source?.lastSuccessfulFetchAt ? asString(source.lastSuccessfulFetchAt) : null,
    lastFailedFetchAt: source?.lastFailedFetchAt ? asString(source.lastFailedFetchAt) : null,
    lastFailureReason: asString(source?.lastFailureReason),
    historicalRelevanceScore: source?.historicalRelevanceScore === null || source?.historicalRelevanceScore === undefined ? null : asNumber(source.historicalRelevanceScore),
    registryNotes: asString(source?.registryNotes),
    crawlMethod: asString(source?.crawlMethod),
    fetchFrequency: asString(source?.fetchFrequency),
    targetedDiscovery: {
      enabled: asBoolean(source?.targetedDiscovery?.enabled),
      crawlMode: asString(source?.targetedDiscovery?.crawlMode) === 'sitemap'
        ? 'sitemap'
        : asString(source?.targetedDiscovery?.crawlMode) === 'category_pages'
          ? 'category_pages'
          : 'rss_only',
      sitemapUrls: asArray<unknown>(source?.targetedDiscovery?.sitemapUrls).map((entry) => asString(entry)).filter(Boolean),
      categoryPageUrls: asArray<unknown>(source?.targetedDiscovery?.categoryPageUrls).map((entry) => asString(entry)).filter(Boolean),
      allowPatterns: asArray<unknown>(source?.targetedDiscovery?.allowPatterns).map((entry) => asString(entry)).filter(Boolean),
      denyPatterns: asArray<unknown>(source?.targetedDiscovery?.denyPatterns).map((entry) => asString(entry)).filter(Boolean),
      maxUrlsPerRun: asNumber(source?.targetedDiscovery?.maxUrlsPerRun || 6),
    },
    lastFetchedAt: source?.lastFetchedAt ? asString(source.lastFetchedAt) : null,
    createdAt: source?.createdAt ? asString(source.createdAt) : undefined,
    updatedAt: source?.updatedAt ? asString(source.updatedAt) : undefined,
  };
}

function normalizeRecommendedTrendSource(source: any): RecommendedTrendSource {
  return {
    key: asString(source?.key),
    group: asString(source?.group),
    name: asString(source?.name),
    type: asString(source?.type) === 'website' ? 'website' : 'rss',
    url: asString(source?.url),
    category: asString(source?.category),
    language: asString(source?.language),
    weight: asNumber(source?.weight),
    focusTags: asArray<unknown>(source?.focusTags).map((entry) => asString(entry)).filter(Boolean),
    notes: asString(source?.notes),
  };
}

function normalizeTrendSourceItem(item: any): TrendSourceItem {
  return {
    _id: asString(item?._id),
    sourceId: asString(item?.sourceId),
    title: asString(item?.title),
    url: asString(item?.url),
    publishedAt: item?.publishedAt ? asString(item.publishedAt) : null,
    cleanedText: asString(item?.cleanedText),
    imageUrls: asArray<unknown>(item?.imageUrls).map((entry) => asString(entry)).filter(Boolean),
    provenance: asString(item?.provenance) === 'feed+discovery'
      ? 'feed+discovery'
      : asString(item?.provenance) === 'discovery'
        ? 'discovery'
        : 'feed',
    originDomain: asString(item?.originDomain),
    status: asString(item?.status),
    contentStatus: asString(item?.contentStatus) === 'full_article_text'
      ? 'full_article_text'
      : asString(item?.contentStatus) === 'teaser_snippet'
        ? 'teaser_snippet'
        : asString(item?.contentStatus) === 'boilerplate_dominated'
          ? 'boilerplate_dominated'
          : asString(item?.contentStatus) === 'requires_review'
            ? 'requires_review'
            : 'unknown',
    reviewStatus: asString(item?.reviewStatus) === 'required'
      ? 'required'
      : asString(item?.reviewStatus) === 'completed'
        ? 'completed'
        : 'not_required',
    qualityAssessment: {
      contentLength: asNumber(item?.qualityAssessment?.contentLength),
      longSentenceCount: asNumber(item?.qualityAssessment?.longSentenceCount),
      uniqueWordCount: asNumber(item?.qualityAssessment?.uniqueWordCount),
      issue: asString(item?.qualityAssessment?.issue),
    },
    extractionMetadata: {
      method: asString(item?.extractionMetadata?.method) === 'raw_text_only'
        ? 'raw_text_only'
        : asString(item?.extractionMetadata?.method) === 'feed_readability'
          ? 'feed_readability'
          : asString(item?.extractionMetadata?.method) === 'feed_html_strip'
            ? 'feed_html_strip'
            : asString(item?.extractionMetadata?.method) === 'http_readability'
              ? 'http_readability'
              : asString(item?.extractionMetadata?.method) === 'http_html_strip'
                ? 'http_html_strip'
                : asString(item?.extractionMetadata?.method) === 'headless_readability'
                  ? 'headless_readability'
                  : asString(item?.extractionMetadata?.method) === 'headless_html_strip'
                    ? 'headless_html_strip'
                    : 'unknown',
      extractedTextLength: asNumber(item?.extractionMetadata?.extractedTextLength),
      wasTruncated: item?.extractionMetadata?.wasTruncated === true,
      sectionCount: asNumber(item?.extractionMetadata?.sectionCount),
      warnings: asArray<unknown>(item?.extractionMetadata?.warnings).map((entry) => asString(entry)).filter(Boolean),
      title: asString(item?.extractionMetadata?.title),
      excerpt: asString(item?.extractionMetadata?.excerpt),
      byline: asString(item?.extractionMetadata?.byline),
      siteName: asString(item?.extractionMetadata?.siteName),
    },
    errorLog: asString(item?.errorLog),
    createdAt: item?.createdAt ? asString(item.createdAt) : undefined,
    updatedAt: item?.updatedAt ? asString(item.updatedAt) : undefined,
    runPresentation: normalizeTrendRunPresentation(item?.runPresentation),
  };
}

function normalizeTrendDiscoveryRunProgress(progress: any): TrendDiscoveryRunProgress {
  return {
    phase: asString(progress?.phase) === 'initializing'
      ? 'initializing'
      : asString(progress?.phase) === 'running_targeted_crawl'
        ? 'running_targeted_crawl'
      : asString(progress?.phase) === 'persisting_candidates'
        ? 'persisting_candidates'
        : asString(progress?.phase) === 'completed'
          ? 'completed'
          : 'running_query',
    message: asString(progress?.message),
    current: progress?.current === null || progress?.current === undefined ? null : asNumber(progress?.current),
    total: progress?.total === null || progress?.total === undefined ? null : asNumber(progress?.total),
    queryKey: asString(progress?.queryKey),
    queryLabel: asString(progress?.queryLabel),
    candidateCount: progress?.candidateCount === null || progress?.candidateCount === undefined ? undefined : asNumber(progress?.candidateCount),
    createdCount: progress?.createdCount === null || progress?.createdCount === undefined ? undefined : asNumber(progress?.createdCount),
    updatedCount: progress?.updatedCount === null || progress?.updatedCount === undefined ? undefined : asNumber(progress?.updatedCount),
  };
}

function normalizeTrendDiscoveryCandidate(candidate: any): TrendDiscoveryCandidate {
  const status = asString(candidate?.status);
  const tier = asString(candidate?.preliminarySourceTier).toUpperCase();
  const layer = asString(candidate?.preliminarySourceLayer);
  const geography = asString(candidate?.preliminaryGeography);
  const sourceType = asString(candidate?.preliminarySourceType);
  const accessType = asString(candidate?.preliminaryAccessType);
  const validationStatus = asString(candidate?.validationStatus);
  return {
    _id: asString(candidate?._id),
    batchKey: asString(candidate?.batchKey),
    trigger: asString(candidate?.trigger) === 'pipeline' ? 'pipeline' : 'manual',
    queryKey: asString(candidate?.queryKey),
    queryLabel: asString(candidate?.queryLabel),
    seedQueryKey: asString(candidate?.seedQueryKey),
    expandedQueryLabel: asString(candidate?.expandedQueryLabel),
    track: asString(candidate?.track),
    category: asString(candidate?.category),
    title: asString(candidate?.title),
    url: asString(candidate?.url),
    domain: asString(candidate?.domain),
    snippet: asString(candidate?.snippet),
    reason: asString(candidate?.reason),
    publishedAt: candidate?.publishedAt ? asString(candidate.publishedAt) : null,
    language: asString(candidate?.language),
    provenance: 'discovery',
    citations: asArray<any>(candidate?.citations).map((citation) => ({
      title: asString(citation?.title),
      url: asString(citation?.url),
    })).filter((citation) => citation.title || citation.url),
    lane: asString(candidate?.lane) === 'curated_feed'
      ? 'curated_feed'
      : asString(candidate?.lane) === 'targeted_domain_crawl'
        ? 'targeted_domain_crawl'
        : asString(candidate?.lane) === 'manual_import'
          ? 'manual_import'
          : 'open_web_search',
    sourceMethod: asString(candidate?.sourceMethod),
    coverageKey: asString(candidate?.coverageKey),
    coverageTags: asArray<unknown>(candidate?.coverageTags).map((entry) => asString(entry)).filter(Boolean),
    confidenceScore: asNumber(candidate?.confidenceScore),
    status: (
      status === 'approved' ||
      status === 'rejected' ||
      status === 'quarantined' ||
      status === 'imported'
    ) ? status : 'pending',
    preliminarySourceTier: (tier === 'A' || tier === 'B' || tier === 'C' ? tier : 'D') as TrendDiscoveryCandidate['preliminarySourceTier'],
    preliminarySourceLayer: (
      layer === 'primary_nordic' ||
      layer === 'secondary_europe' ||
      layer === 'inspiration_global'
    ) ? layer : 'weak_support',
    preliminaryGeography: (
      geography === 'sweden' ||
      geography === 'nordics' ||
      geography === 'europe' ||
      geography === 'usa_uk' ||
      geography === 'global'
    ) ? geography : 'unknown',
    preliminarySourceType: (
      sourceType === 'industry_media' ||
      sourceType === 'official_statistics' ||
      sourceType === 'retail_chain' ||
      sourceType === 'foodservice' ||
      sourceType === 'packaging' ||
      sourceType === 'design_media' ||
      sourceType === 'social_recipe' ||
      sourceType === 'authority' ||
      sourceType === 'blog'
    ) ? sourceType : 'other',
    preliminaryAccessType: (
      accessType === 'fulltext' ||
      accessType === 'partial' ||
      accessType === 'snippet_only' ||
      accessType === 'rss_only' ||
      accessType === 'paywall' ||
      accessType === 'pdf_report'
    ) ? accessType : 'unknown',
    validationStatus: (
      validationStatus === 'validated' ||
      validationStatus === 'needs_ab_validation' ||
      validationStatus === 'weak_support'
    ) ? validationStatus : 'needs_manual_review',
    validationReason: asString(candidate?.validationReason),
    suggestedValidationSourceTypes: asArray<unknown>(candidate?.suggestedValidationSourceTypes).map((entry) => asString(entry)).filter(Boolean),
    suggestedValidationSearches: asArray<unknown>(candidate?.suggestedValidationSearches).map((entry) => asString(entry)).filter(Boolean),
    inspirationOnly: asBoolean(candidate?.inspirationOnly),
    evidenceWeight: asNumber(candidate?.evidenceWeight),
    notes: asString(candidate?.notes),
    sourceItemId: candidate?.sourceItemId ? asString(candidate.sourceItemId) : null,
    seenCount: asNumber(candidate?.seenCount),
    updatedAt: candidate?.updatedAt ? asString(candidate.updatedAt) : undefined,
  };
}

function normalizeTrendDiscoveryQuery(query: any): TrendDiscoveryQuery {
  return {
    _id: asString(query?._id),
    key: asString(query?.key),
    label: asString(query?.label),
    track: asString(query?.track),
    category: asString(query?.category),
    language: asString(query?.language) === 'en' ? 'en' : 'sv',
    prompt: asString(query?.prompt),
    priority: asNumber(query?.priority),
    active: asBoolean(query?.active),
    version: asNumber(query?.version),
    createdAt: query?.createdAt ? asString(query.createdAt) : undefined,
    updatedAt: query?.updatedAt ? asString(query.updatedAt) : undefined,
  };
}

function normalizeTrendTestSetSummary(testSet: any): TrendTestSetSummary {
  return {
    key: asString(testSet?.key),
    label: asString(testSet?.label),
    description: asString(testSet?.description),
    recommendedProfile: asString(testSet?.recommendedProfile) === 'mini' ? 'mini' : 'micro',
    scenarioTags: asArray<unknown>(testSet?.scenarioTags).map((entry) => asString(entry)).filter(Boolean),
    categories: asArray<unknown>(testSet?.categories).map((entry) => asString(entry)).filter(Boolean),
    itemCount: asNumber(testSet?.itemCount),
    createdAt: asString(testSet?.createdAt),
    updatedAt: asString(testSet?.updatedAt || testSet?.createdAt),
  };
}

function normalizeTrendSettings(settings: any): TrendModuleSettings {
  return {
    enabled: asBoolean(settings?.enabled),
    schedulerEnabled: asBoolean(settings?.schedulerEnabled),
    scheduleDay: 'monday',
    scheduleTime: asString(settings?.scheduleTime || '05:00'),
    categoryRoots: {
      products: settings?.categoryRoots?.products ? asString(settings.categoryRoots.products) : null,
      recipes: settings?.categoryRoots?.recipes ? asString(settings.categoryRoots.recipes) : null,
      designPackaging: settings?.categoryRoots?.designPackaging ? asString(settings.categoryRoots.designPackaging) : null,
      foodTrends: settings?.categoryRoots?.foodTrends ? asString(settings.categoryRoots.foodTrends) : null,
      marketSignals: settings?.categoryRoots?.marketSignals ? asString(settings.categoryRoots.marketSignals) : null,
    },
    sourceLimits: {
      maxSourcesPerRun: asNumber(settings?.sourceLimits?.maxSourcesPerRun),
      maxSourceItemsPerSource: asNumber(settings?.sourceLimits?.maxSourceItemsPerSource),
    },
    ai: {
      deepResearchModel: asString(settings?.ai?.deepResearchModel),
      analysisModel: asString(settings?.ai?.analysisModel),
      verificationModel: asString(settings?.ai?.verificationModel),
      deepResearchWebSearch: asBoolean(settings?.ai?.deepResearchWebSearch),
      verificationWebSearch: asBoolean(settings?.ai?.verificationWebSearch),
      backgroundResearch: asBoolean(settings?.ai?.backgroundResearch),
      reasoningEffort: asString(settings?.ai?.reasoningEffort) === 'low'
        ? 'low'
        : asString(settings?.ai?.reasoningEffort) === 'medium'
          ? 'medium'
          : 'high',
    },
    matching: {
      productCandidateLimit: asNumber(settings?.matching?.productCandidateLimit),
      recipeCandidateLimit: asNumber(settings?.matching?.recipeCandidateLimit),
      strongMatchScore: asNumber(settings?.matching?.strongMatchScore),
      weakMatchScore: asNumber(settings?.matching?.weakMatchScore),
    },
    fontanaContext: {
      companyName: asString(settings?.fontanaContext?.companyName),
      outputLanguage: 'sv',
      primaryMarket: asString(settings?.fontanaContext?.primaryMarket),
      activeMarkets: asArray<unknown>(settings?.fontanaContext?.activeMarkets).map((entry) => asString(entry)).filter(Boolean),
      salesChannels: asArray<unknown>(settings?.fontanaContext?.salesChannels).map((entry) => asString(entry)).filter(Boolean),
      coreOrigins: asArray<unknown>(settings?.fontanaContext?.coreOrigins).map((entry) => asString(entry)).filter(Boolean),
      pricePosition: asString(settings?.fontanaContext?.pricePosition),
      differentiators: asArray<unknown>(settings?.fontanaContext?.differentiators).map((entry) => asString(entry)).filter(Boolean),
      priorityCategories: asArray<unknown>(settings?.fontanaContext?.priorityCategories).map((entry) => asString(entry)).filter(Boolean),
      priorityIngredients: asArray<unknown>(settings?.fontanaContext?.priorityIngredients).map((entry) => asString(entry)).filter(Boolean),
      recipeFocus: asArray<unknown>(settings?.fontanaContext?.recipeFocus).map((entry) => asString(entry)).filter(Boolean),
      growthGoals: asArray<unknown>(settings?.fontanaContext?.growthGoals).map((entry) => asString(entry)).filter(Boolean),
      healthPositioning: asString(settings?.fontanaContext?.healthPositioning),
      avoidThemes: asArray<unknown>(settings?.fontanaContext?.avoidThemes).map((entry) => asString(entry)).filter(Boolean),
      dvhFocus: asArray<unknown>(settings?.fontanaContext?.dvhFocus).map((entry) => asString(entry)).filter(Boolean),
      foodserviceFocus: asArray<unknown>(settings?.fontanaContext?.foodserviceFocus).map((entry) => asString(entry)).filter(Boolean),
    },
    sourceStrategy: {
      maxSignalsToVerify: asNumber(settings?.sourceStrategy?.maxSignalsToVerify),
      maxSignalsToMatch: asNumber(settings?.sourceStrategy?.maxSignalsToMatch),
      discoveryQueryLimit: asNumber(settings?.sourceStrategy?.discoveryQueryLimit),
      discoveryExpansionPerQuery: asNumber(settings?.sourceStrategy?.discoveryExpansionPerQuery),
      discoveryMinConfidenceScore: asNumber(settings?.sourceStrategy?.discoveryMinConfidenceScore),
      discoveryDomainDiversityTarget: asNumber(settings?.sourceStrategy?.discoveryDomainDiversityTarget),
      discoveryAllowedLanguages: asArray<unknown>(settings?.sourceStrategy?.discoveryAllowedLanguages).map((entry) => asString(entry)).filter(Boolean),
      maxSignalAgeDays: asNumber(settings?.sourceStrategy?.maxSignalAgeDays),
      allowUndatedSourceItems: asBoolean(settings?.sourceStrategy?.allowUndatedSourceItems),
      prioritizeRecipeSignals: asBoolean(settings?.sourceStrategy?.prioritizeRecipeSignals),
      prioritizeNordicSources: asBoolean(settings?.sourceStrategy?.prioritizeNordicSources),
      prioritizeFoodserviceSignals: asBoolean(settings?.sourceStrategy?.prioritizeFoodserviceSignals),
      includeSocialRecipePilot: asBoolean(settings?.sourceStrategy?.includeSocialRecipePilot),
      autoImportValidatedCandidates: asBoolean(settings?.sourceStrategy?.autoImportValidatedCandidates),
      enforceDiscoveryLanguageAllowlist: asBoolean(settings?.sourceStrategy?.enforceDiscoveryLanguageAllowlist),
      enforceDomainDiversity: asBoolean(settings?.sourceStrategy?.enforceDomainDiversity),
      sourceRankingWeights: {
        geography: asNumber(settings?.sourceStrategy?.sourceRankingWeights?.geography),
        tier: asNumber(settings?.sourceStrategy?.sourceRankingWeights?.tier),
        layer: asNumber(settings?.sourceStrategy?.sourceRankingWeights?.layer),
        evidenceWeight: asNumber(settings?.sourceStrategy?.sourceRankingWeights?.evidenceWeight),
        freshness: asNumber(settings?.sourceStrategy?.sourceRankingWeights?.freshness),
      },
      prioritizedThemes: asArray<unknown>(settings?.sourceStrategy?.prioritizedThemes).map((entry) => asString(entry)).filter(Boolean),
      socialPilotTerms: asArray<unknown>(settings?.sourceStrategy?.socialPilotTerms).map((entry) => asString(entry)).filter(Boolean),
      designFocusAreas: asArray<unknown>(settings?.sourceStrategy?.designFocusAreas).map((entry) => asString(entry)).filter(Boolean),
      consumerInsightFocus: asArray<unknown>(settings?.sourceStrategy?.consumerInsightFocus).map((entry) => asString(entry)).filter(Boolean),
    },
    updatedAt: settings?.updatedAt ? asString(settings.updatedAt) : null,
  };
}

function normalizeTrendDiscoveryCoverageSummary(data: any): TrendDiscoveryCoverageSummary {
  const normalizeStatus = (value: unknown): 'covered' | 'partial' | 'missing' => {
    const status = asString(value);
    if (status === 'covered' || status === 'partial') return status;
    return 'missing';
  };
  const normalizeCoverageLane = (
    value: unknown,
  ): TrendDiscoveryCoverageContractLane['lane'] => {
    const lane = asString(value);
    if (lane === 'curated_feed') return 'curated_feed';
    if (lane === 'targeted_domain_crawl') return 'targeted_domain_crawl';
    if (lane === 'manual_import') return 'manual_import';
    return 'open_web_search';
  };
  const fallbackContract: TrendDiscoveryCoverageContract = {
    title: 'Så discovery fungerar nu',
    summary: 'Discovery kombinerar AI-webbsök över öppna källor med riktad domäncrawl för ett litet antal kuraterade sajter. Kuraterade feeds hämtas i ordinarie körningar och manuella importer styrs redaktionellt. Det här är inte en full crawl av hela webben.',
    lanes: [
      {
        lane: 'curated_feed' as const,
        label: 'Kuraterade källor',
        description: 'Redaktionellt valda feeds, webbplatser och partnerkällor som hämtas i ordinarie trendkörningar.',
      },
      {
        lane: 'open_web_search' as const,
        label: 'AI-webbsök',
        description: 'AI-stödd webbresearch över öppna källor utifrån seedade discovery-frågor och deterministisk query expansion.',
      },
      {
        lane: 'targeted_domain_crawl' as const,
        label: 'Riktad domäncrawl',
        description: 'Begränsad sitemap- eller kategorisidescrawl för ett litet antal utvalda sajter med definierade regler.',
      },
      {
        lane: 'manual_import' as const,
        label: 'Manuell import',
        description: 'Redaktionella fynd som väljs, granskas och importeras manuellt i stället för att hämtas automatiskt.',
      },
    ],
    statuses: [
      {
        key: 'covered' as const,
        label: 'Täckt',
        description: 'Ytan gav tydlig träffbild med mer än enstaka spår eller bredare domänspridning.',
      },
      {
        key: 'partial' as const,
        label: 'Delvis täckt',
        description: 'Ytan gav viss träff, men den var smal, tunn eller beroende av få domäner.',
      },
      {
        key: 'missing' as const,
        label: 'Saknas',
        description: 'Ytan gav ingen tydlig träff eller kördes inte i den aktuella discoveryomgången.',
      },
    ],
  };
  const contractLanes: TrendDiscoveryCoverageContractLane[] = asArray<any>(data?.contract?.lanes).map((entry): TrendDiscoveryCoverageContractLane => ({
    lane: normalizeCoverageLane(entry?.lane),
    label: asString(entry?.label),
    description: asString(entry?.description),
  })).filter((entry) => entry.label);
  const contractStatuses: TrendDiscoveryCoverageContractStatus[] = asArray<any>(data?.contract?.statuses).map((entry) => ({
    key: normalizeStatus(entry?.key),
    label: asString(entry?.label),
    description: asString(entry?.description),
  })).filter((entry) => entry.label);

  return {
    contract: {
      title: asString(data?.contract?.title) || fallbackContract.title,
      summary: asString(data?.contract?.summary) || fallbackContract.summary,
      lanes: contractLanes.length > 0 ? contractLanes : fallbackContract.lanes,
      statuses: contractStatuses.length > 0 ? contractStatuses : fallbackContract.statuses,
    },
    activeQueryCount: asNumber(data?.activeQueryCount),
    executedQueryCount: asNumber(data?.executedQueryCount),
    skippedQueryKeys: asArray<unknown>(data?.skippedQueryKeys).map((entry) => asString(entry)).filter(Boolean),
    emptyQueryKeys: asArray<unknown>(data?.emptyQueryKeys).map((entry) => asString(entry)).filter(Boolean),
    targetedProfileCount: asNumber(data?.targetedProfileCount),
    executedTargetedProfileCount: asNumber(data?.executedTargetedProfileCount),
    laneSummaries: asArray<any>(data?.laneSummaries).map((entry) => ({
      lane: asString(entry?.lane) === 'curated_feed'
        ? 'curated_feed'
        : asString(entry?.lane) === 'targeted_domain_crawl'
          ? 'targeted_domain_crawl'
          : asString(entry?.lane) === 'manual_import'
            ? 'manual_import'
            : 'open_web_search',
      label: asString(entry?.label),
      description: asString(entry?.description),
      configuredCount: asNumber(entry?.configuredCount),
      executedCount: asNumber(entry?.executedCount),
      candidateCount: asNumber(entry?.candidateCount),
      pendingCount: asNumber(entry?.pendingCount),
      quarantinedCount: asNumber(entry?.quarantinedCount),
      createdCount: asNumber(entry?.createdCount),
      updatedCount: asNumber(entry?.updatedCount),
      uniqueDomainCount: asNumber(entry?.uniqueDomainCount),
      status: normalizeStatus(entry?.status),
    })),
    byQuery: asArray<any>(data?.byQuery).map((entry) => ({
      coverageKey: asString(entry?.coverageKey),
      lane: asString(entry?.lane) === 'curated_feed'
        ? 'curated_feed'
        : asString(entry?.lane) === 'targeted_domain_crawl'
          ? 'targeted_domain_crawl'
          : asString(entry?.lane) === 'manual_import'
            ? 'manual_import'
            : 'open_web_search',
      queryKey: asString(entry?.queryKey),
      queryLabel: asString(entry?.queryLabel),
      seedQueryKey: asString(entry?.seedQueryKey),
      track: asString(entry?.track),
      category: asString(entry?.category),
      status: normalizeStatus(entry?.status),
      executedVariantCount: asNumber(entry?.executedVariantCount),
      candidateCount: asNumber(entry?.candidateCount),
      pendingCount: asNumber(entry?.pendingCount),
      quarantinedCount: asNumber(entry?.quarantinedCount),
      createdCount: asNumber(entry?.createdCount),
      updatedCount: asNumber(entry?.updatedCount),
      uniqueDomains: asArray<unknown>(entry?.uniqueDomains).map((value) => asString(value)).filter(Boolean),
      expandedQueryLabels: asArray<unknown>(entry?.expandedQueryLabels).map((value) => asString(value)).filter(Boolean),
      coverageTags: asArray<unknown>(entry?.coverageTags).map((value) => asString(value)).filter(Boolean),
      sourceMethods: asArray<unknown>(entry?.sourceMethods).map((value) => asString(value)).filter(Boolean),
      empty: asBoolean(entry?.empty),
      skipped: asBoolean(entry?.skipped),
    })),
    byTrack: asArray<any>(data?.byTrack).map((entry) => ({
      coverageKey: asString(entry?.coverageKey),
      label: asString(entry?.label),
      status: normalizeStatus(entry?.status),
      expectedCount: asNumber(entry?.expectedCount),
      coveredCount: asNumber(entry?.coveredCount),
      partialCount: asNumber(entry?.partialCount),
      missingCount: asNumber(entry?.missingCount),
      candidateCount: asNumber(entry?.candidateCount),
      uniqueDomains: asArray<unknown>(entry?.uniqueDomains).map((value) => asString(value)).filter(Boolean),
    })),
    byCategory: asArray<any>(data?.byCategory).map((entry) => ({
      coverageKey: asString(entry?.coverageKey),
      label: asString(entry?.label),
      status: normalizeStatus(entry?.status),
      expectedCount: asNumber(entry?.expectedCount),
      coveredCount: asNumber(entry?.coveredCount),
      partialCount: asNumber(entry?.partialCount),
      missingCount: asNumber(entry?.missingCount),
      candidateCount: asNumber(entry?.candidateCount),
      uniqueDomains: asArray<unknown>(entry?.uniqueDomains).map((value) => asString(value)).filter(Boolean),
    })),
    byDomain: asArray<any>(data?.byDomain).map((entry) => ({
      coverageKey: asString(entry?.coverageKey),
      domain: asString(entry?.domain),
      status: normalizeStatus(entry?.status),
      candidateCount: asNumber(entry?.candidateCount),
      laneCount: asNumber(entry?.laneCount),
      lanes: asArray<unknown>(entry?.lanes).map((value) => (
        asString(value) === 'curated_feed'
          ? 'curated_feed'
          : asString(value) === 'targeted_domain_crawl'
            ? 'targeted_domain_crawl'
            : asString(value) === 'manual_import'
              ? 'manual_import'
              : 'open_web_search'
      )),
      queryKeys: asArray<unknown>(entry?.queryKeys).map((value) => asString(value)).filter(Boolean),
    })),
    uniqueDomains: asArray<unknown>(data?.uniqueDomains).map((entry) => asString(entry)).filter(Boolean),
    missingTracks: asArray<unknown>(data?.missingTracks).map((entry) => asString(entry)).filter(Boolean),
    missingCategories: asArray<unknown>(data?.missingCategories).map((entry) => asString(entry)).filter(Boolean),
  };
}

function normalizeTrendDiscoveryRunSummary(data: any): TrendDiscoveryRunSummary {
  return {
    batchKey: asString(data?.batchKey),
    trigger: asString(data?.trigger) === 'pipeline' ? 'pipeline' : 'manual',
    queryCount: asNumber(data?.queryCount),
    activeQueryCount: asNumber(data?.activeQueryCount),
    skippedQueryCount: asNumber(data?.skippedQueryCount),
    crawlProfileCount: asNumber(data?.crawlProfileCount),
    executedCrawlProfileCount: asNumber(data?.executedCrawlProfileCount),
    candidateCount: asNumber(data?.candidateCount),
    pendingCount: asNumber(data?.pendingCount),
    quarantinedCount: asNumber(data?.quarantinedCount),
    createdCount: asNumber(data?.createdCount),
    updatedCount: asNumber(data?.updatedCount),
    coverage: normalizeTrendDiscoveryCoverageSummary(data?.coverage),
  };
}

function normalizeTrendDiscoveryRunState(data: any): TrendDiscoveryRunState {
  const status = asString(data?.status);
  return {
    status: (
      status === 'running'
      || status === 'completed'
      || status === 'failed'
    ) ? status : 'idle',
    batchKey: data?.batchKey ? asString(data.batchKey) : null,
    startedAt: data?.startedAt ? asString(data.startedAt) : null,
    finishedAt: data?.finishedAt ? asString(data.finishedAt) : null,
    progress: data?.progress ? normalizeTrendDiscoveryRunProgress(data.progress) : null,
    summary: data?.summary ? normalizeTrendDiscoveryRunSummary(data.summary) : null,
    error: coerceTrendUiText(data?.error),
  };
}

function normalizeTrendsOverview(data: any): TrendsOverview {
  const scopeResolutions = typeof data?.scopeResolutions === 'object' && data?.scopeResolutions
    ? Object.fromEntries(
      Object.entries(data.scopeResolutions).map(([key, value]: [string, any]) => [
        key,
        {
          key: asString(value?.key || key),
          configuredRootId: value?.configuredRootId ? asString(value.configuredRootId) : null,
          resolvedRootId: value?.resolvedRootId ? asString(value.resolvedRootId) : null,
          resolvedRootName: value?.resolvedRootName ? asString(value.resolvedRootName) : null,
          fallbackRootName: asString(value?.fallbackRootName),
          source: value?.source === 'configured' || value?.source === 'fallback' ? value.source : 'missing',
        },
      ]),
    )
    : {};

  return {
    report: data?.report ? normalizeTrendReport(data.report) : null,
    briefing: data?.briefing ? normalizeIntelligenceBriefing(data.briefing) : null,
    heroStory: data?.heroStory ? normalizeTrendStory(data.heroStory) : null,
    heroArticle: data?.heroArticle ? normalizeTrendArticle(data.heroArticle) : null,
    weeklyHighlights: asArray<any>(data?.weeklyHighlights).map(normalizeTrendArticle),
    topStories: asArray<any>(data?.topStories).map(normalizeTrendStory),
    topSignals: asArray<any>(data?.topSignals).map(normalizeTrendSignal),
    topOpportunities: asArray<any>(data?.topOpportunities).map(normalizeTrendOpportunity),
    latestArticles: asArray<any>(data?.latestArticles).map(normalizeTrendArticle),
    latestRun: data?.latestRun ? normalizeTrendPipelineRun(data.latestRun) : null,
    scopeResolutions,
  };
}

export async function fetchTrendsOverview(): Promise<TrendsOverview> {
  const { data } = await axiosInstance.get(`${BASE_URL}/overview`);
  return normalizeTrendsOverview(data);
}

export async function fetchTrendSettings(): Promise<TrendModuleSettings> {
  const { data } = await axiosInstance.get(`${BASE_URL}/settings`);
  return normalizeTrendSettings(data);
}

export async function saveTrendSettings(payload: Partial<TrendModuleSettings>): Promise<TrendModuleSettings> {
  const { data } = await axiosInstance.put(`${BASE_URL}/settings`, payload);
  return normalizeTrendSettings(data);
}

export async function fetchTrendResetPreview(): Promise<TrendResetPreview> {
  const { data } = await axiosInstance.get(`${BASE_URL}/admin/reset-preview`);
  return normalizeTrendResetPreview(data);
}

export async function createTrendResetSnapshot(): Promise<TrendResetSnapshotResult> {
  const { data } = await axiosInstance.post(`${BASE_URL}/admin/reset-snapshot`);
  return normalizeTrendResetSnapshotResult(data);
}

export async function downloadTrendResetSnapshot(snapshotId: string): Promise<Blob> {
  const response = await axiosInstance.get(`${BASE_URL}/admin/reset-snapshots/${encodeURIComponent(snapshotId)}/download`, {
    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function hardResetTrendData(payload: {
  snapshotId: string;
  confirmText: string;
  restartPipeline?: boolean;
}): Promise<TrendHardResetResult> {
  const { data } = await axiosInstance.post(`${BASE_URL}/admin/hard-reset`, payload);
  return normalizeTrendHardResetResult(data);
}

export async function fetchTrendRuns(limit = 20): Promise<TrendPipelineRun[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/runs`, { params: { limit } });
  return asArray<any>(data).map(normalizeTrendPipelineRun);
}

export async function fetchTrendTestSets(): Promise<TrendTestSetSummary[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/test-sets`);
  return asArray<any>(data).map(normalizeTrendTestSetSummary);
}

export async function fetchTrendPilotRunPresets(): Promise<TrendPilotRunPreset[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/runs/pilot-presets`);
  const normalizePresetKey = (value: unknown): TrendPilotRunPreset['key'] => {
    const key = asString(value);
    if (
      key === 'recipes_social_preview' ||
      key === 'design_packaging_preview' ||
      key === 'dvh_market_preview' ||
      key === 'foodservice_preview' ||
      key === 'brand_radar_preview' ||
      key === 'single_source_test' ||
      key === 'single_discovery_query_preview' ||
      key === 'report_only_briefing' ||
      key === 'second_opinion_only' ||
      key === 'prompt_test_previous_material' ||
      key === 'model_test_same_material'
    ) return key;
    return 'micro_test_primary_source';
  };
  return asArray<any>(data).map((preset) => ({
    key: normalizePresetKey(preset?.key),
    label: asString(preset?.label),
    description: asString(preset?.description),
    request: preset?.request || {},
    plan: normalizeTrendRunPlan(preset?.plan),
  }));
}

export async function planTrendRun(payload: TrendRunRequest): Promise<TrendRunPlan> {
  const { data } = await axiosInstance.post(`${BASE_URL}/runs/plan`, payload || {});
  return normalizeTrendRunPlan(data);
}

export async function fetchTrendRun(runId: string): Promise<TrendPipelineRun> {
  const { data } = await axiosInstance.get(`${BASE_URL}/runs/${runId}`);
  return normalizeTrendPipelineRun(data);
}

export async function fetchTrendRunPreviewResults(runId: string): Promise<TrendPreviewRunArtifact> {
  const { data } = await axiosInstance.get(`${BASE_URL}/runs/${runId}/preview-results`);
  return normalizeTrendPreviewRunArtifact(data);
}

export async function runTrendPipelineNow(payload?: TrendPipelineStartRequest): Promise<TrendPipelineRun> {
  const { data } = await axiosInstance.post(`${BASE_URL}/runs/manual`, payload || {});
  return normalizeTrendPipelineRun(data?.run ?? data);
}

export async function startPlannedTrendRun(payload: TrendRunRequest): Promise<{ run: TrendPipelineRun; plan: TrendRunPlan }> {
  const { data } = await axiosInstance.post(`${BASE_URL}/runs/start`, payload || {});
  return {
    run: normalizeTrendPipelineRun(data?.run),
    plan: normalizeTrendRunPlan(data?.plan),
  };
}

export async function fetchBrandRadarHits(params?: {
  includeFalsePositives?: boolean;
  classification?: TrendBrandRadarHit['classification'];
  limit?: number;
}): Promise<TrendBrandRadarHit[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/brand-radar/hits`, { params });
  return asArray<any>(data).map(normalizeBrandRadarHit);
}

export async function updateBrandRadarHit(hitId: string, payload: Partial<Pick<TrendBrandRadarHit, 'isFalsePositive' | 'falsePositiveReason' | 'classification' | 'sentiment' | 'reviewNote'>>): Promise<TrendBrandRadarHit> {
  const { data } = await axiosInstance.patch(`${BASE_URL}/brand-radar/hits/${hitId}`, payload || {});
  return normalizeBrandRadarHit(data);
}

export async function cancelTrendRun(runId: string): Promise<TrendPipelineRun> {
  const { data } = await axiosInstance.post(`${BASE_URL}/runs/${runId}/cancel`);
  return normalizeTrendPipelineRun(data);
}

export async function fetchTrendSources(): Promise<TrendSource[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/sources`);
  return asArray<any>(data).map(normalizeTrendSource);
}

export async function fetchRecommendedTrendSources(): Promise<RecommendedTrendSource[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/sources/recommended`);
  return asArray<any>(data).map(normalizeRecommendedTrendSource);
}

export async function importRecommendedTrendSources(keys?: string[]): Promise<{ createdCount: number; skippedCount: number }> {
  const { data } = await axiosInstance.post(`${BASE_URL}/sources/recommended/import`, { keys });
  return {
    createdCount: asArray<any>(data?.created).length,
    skippedCount: asArray<any>(data?.skipped).length,
  };
}

export async function createTrendSource(payload: Partial<TrendSource>): Promise<TrendSource> {
  const { data } = await axiosInstance.post(`${BASE_URL}/sources`, payload);
  return normalizeTrendSource(data);
}

export async function updateTrendSource(sourceId: string, payload: Partial<TrendSource>): Promise<TrendSource> {
  const { data } = await axiosInstance.put(`${BASE_URL}/sources/${sourceId}`, payload);
  return normalizeTrendSource(data);
}

export async function deleteTrendSource(sourceId: string): Promise<void> {
  await axiosInstance.delete(`${BASE_URL}/sources/${sourceId}`);
}

export async function fetchTrendSourceItems(params?: { sourceId?: string; status?: string; limit?: number; offset?: number }): Promise<{ items: TrendSourceItem[]; total: number }> {
  const { data } = await axiosInstance.get(`${BASE_URL}/source-items`, { params });
  return {
    items: asArray<any>(data?.items).map(normalizeTrendSourceItem),
    total: asNumber(data?.total),
  };
}

export async function fetchTrendDiscoveryCandidates(params?: {
  status?: string;
  category?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: TrendDiscoveryCandidate[]; total: number }> {
  const { data } = await axiosInstance.get(`${BASE_URL}/discovery/candidates`, { params });
  return {
    items: asArray<any>(data?.items).map(normalizeTrendDiscoveryCandidate),
    total: asNumber(data?.total),
  };
}

export async function fetchTrendDiscoveryQueries(params?: {
  activeOnly?: boolean;
  limit?: number;
}): Promise<TrendDiscoveryQuery[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/discovery/queries`, { params });
  return asArray<any>(data).map(normalizeTrendDiscoveryQuery);
}

export async function createTrendDiscoveryQuery(payload: Partial<TrendDiscoveryQuery>): Promise<TrendDiscoveryQuery> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/queries`, payload);
  return normalizeTrendDiscoveryQuery(data);
}

export async function updateTrendDiscoveryQuery(
  queryId: string,
  payload: Partial<TrendDiscoveryQuery>,
): Promise<TrendDiscoveryQuery> {
  const { data } = await axiosInstance.put(`${BASE_URL}/discovery/queries/${queryId}`, payload);
  return normalizeTrendDiscoveryQuery(data);
}

export async function runTrendDiscoveryNow(): Promise<TrendDiscoveryRunState> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/run`);
  return normalizeTrendDiscoveryRunState(data);
}

export async function fetchTrendDiscoveryStatus(): Promise<TrendDiscoveryRunState> {
  const { data } = await axiosInstance.get(`${BASE_URL}/discovery/status`);
  return normalizeTrendDiscoveryRunState(data);
}

export async function backfillTrendImages(): Promise<TrendImageBackfillSummary> {
  const { data } = await axiosInstance.post(`${BASE_URL}/images/backfill`);
  return {
    normalizedSourceItems: asNumber(data?.normalizedSourceItems),
    enrichedSourceItems: asNumber(data?.enrichedSourceItems),
    updatedArticles: asNumber(data?.updatedArticles),
    updatedStories: asNumber(data?.updatedStories),
  };
}

export async function archiveTrendArticlesBefore(before: string): Promise<{ archivedCount: number }> {
  const { data } = await axiosInstance.post(`${BASE_URL}/articles/archive-before`, { before });
  return { archivedCount: asNumber(data?.archivedCount) };
}

export async function approveTrendDiscoveryCandidate(candidateId: string): Promise<TrendDiscoveryCandidate> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/${candidateId}/approve`);
  return normalizeTrendDiscoveryCandidate(data);
}

export async function rejectTrendDiscoveryCandidate(candidateId: string): Promise<TrendDiscoveryCandidate> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/${candidateId}/reject`);
  return normalizeTrendDiscoveryCandidate(data);
}

export async function quarantineTrendDiscoveryCandidate(candidateId: string): Promise<TrendDiscoveryCandidate> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/${candidateId}/quarantine`);
  return normalizeTrendDiscoveryCandidate(data);
}

export async function importTrendDiscoveryCandidate(candidateId: string): Promise<TrendDiscoveryCandidate> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/${candidateId}/import`);
  return normalizeTrendDiscoveryCandidate(data);
}

export async function createTrendDiscoverySourceDraft(candidateId: string): Promise<TrendSource> {
  const { data } = await axiosInstance.post(`${BASE_URL}/discovery/${candidateId}/source-draft`);
  return normalizeTrendSource(data);
}

export async function fetchTrendReports(status?: string): Promise<TrendReport[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/reports`, { params: { status } });
  return asArray<any>(data).map(normalizeTrendReport);
}

export async function fetchTrendReport(reportId: string): Promise<TrendReport> {
  const { data } = await axiosInstance.get(`${BASE_URL}/reports/${reportId}`);
  return normalizeTrendReport(data);
}

export async function generateTrendReport(issueDate?: string): Promise<TrendReport> {
  const { data } = await axiosInstance.post(`${BASE_URL}/reports/generate`, { issueDate });
  return normalizeTrendReport(data);
}

export async function updateTrendReport(reportId: string, payload: Partial<TrendReport>): Promise<TrendReport> {
  const { data } = await axiosInstance.put(`${BASE_URL}/reports/${reportId}`, payload);
  return normalizeTrendReport(data);
}

export async function publishTrendReport(reportId: string): Promise<TrendReport> {
  const { data } = await axiosInstance.post(`${BASE_URL}/reports/${reportId}/publish`);
  return normalizeTrendReport(data);
}

export async function fetchTrendArticles(params?: {
  category?: string;
  status?: string;
  fromRun?: string;
  minDate?: string;
  qualityScope?: TrendQualityScope;
}): Promise<TrendArticle[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/articles`, { params });
  return asArray<any>(data).map(normalizeTrendArticle);
}

export async function fetchTrendStories(params?: {
  category?: string;
  storyType?: string;
  designSubCategory?: string;
  audience?: string;
  behavior?: string;
  usageOccasion?: string;
  qualityScope?: TrendQualityScope;
}): Promise<TrendStory[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/stories`, { params });
  return asArray<any>(data).map(normalizeTrendStory);
}

export async function fetchTrendCards(params?: {
  status?: string;
  lane?: string;
  runId?: string;
  qualityStatus?: string;
  limit?: number;
}): Promise<TrendCard[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/cards`, { params });
  return asArray<any>(data).map(normalizeTrendCard);
}

export async function fetchTrendCard(cardId: string): Promise<TrendCard> {
  const { data } = await axiosInstance.get(`${BASE_URL}/cards/${cardId}`);
  return normalizeTrendCard(data);
}

export async function fetchTrendStory(storyId: string, qualityScope: TrendQualityScope = 'publishable'): Promise<TrendStory> {
  const { data } = await axiosInstance.get(`${BASE_URL}/stories/${storyId}`, { params: { qualityScope } });
  return normalizeTrendStory(data);
}

export async function fetchTrendArticle(articleId: string, qualityScope: TrendQualityScope = 'publishable'): Promise<TrendArticle> {
  const { data } = await axiosInstance.get(`${BASE_URL}/articles/${articleId}`, { params: { qualityScope } });
  return normalizeTrendArticle(data);
}

export async function updateTrendArticle(articleId: string, payload: Partial<TrendArticle>): Promise<TrendArticle> {
  const { data } = await axiosInstance.put(`${BASE_URL}/articles/${articleId}`, payload);
  return normalizeTrendArticle(data);
}

export async function featureTrendArticle(articleId: string, featured = true): Promise<TrendArticle> {
  const { data } = await axiosInstance.post(`${BASE_URL}/articles/${articleId}/feature`, { featured });
  return normalizeTrendArticle(data);
}

export async function hideTrendArticle(articleId: string, hidden = true): Promise<TrendArticle> {
  const { data } = await axiosInstance.post(`${BASE_URL}/articles/${articleId}/hide`, { hidden });
  return normalizeTrendArticle(data);
}

export async function fetchTrendSignals(): Promise<TrendSignal[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/signals`);
  return asArray<any>(data).map(normalizeTrendSignal);
}

export async function fetchTrendSignalHistory(signalId: string): Promise<TrendSignalSnapshot[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/signals/${signalId}/history`);
  return asArray<any>(data).map((snapshot) => ({
    _id: asString(snapshot?._id),
    signalId: asString(snapshot?.signalId),
    issueDate: asString(snapshot?.issueDate),
    mentionCount: asNumber(snapshot?.mentionCount),
    strengthScore: asNumber(snapshot?.strengthScore),
    momentumScore: asNumber(snapshot?.momentumScore),
    noveltyScore: asNumber(snapshot?.noveltyScore),
    status: asString(snapshot?.status) === 'new'
      ? 'new'
      : asString(snapshot?.status) === 'rising'
        ? 'rising'
        : asString(snapshot?.status) === 'declining'
          ? 'declining'
          : 'stable',
    recurringCount: asNumber(snapshot?.recurringCount),
    changeInStrength: asNumber(snapshot?.changeInStrength),
    changeInMomentum: asNumber(snapshot?.changeInMomentum),
  }));
}

export async function fetchTrendOpportunities(type?: string): Promise<TrendOpportunity[]> {
  const { data } = await axiosInstance.get(`${BASE_URL}/opportunities`, { params: { type } });
  return asArray<any>(data).map(normalizeTrendOpportunity);
}

export async function updateTrendOpportunity(opportunityId: string, payload: Partial<TrendOpportunity>): Promise<TrendOpportunity> {
  const { data } = await axiosInstance.put(`${BASE_URL}/opportunities/${opportunityId}`, payload);
  return normalizeTrendOpportunity(data);
}

export async function fetchTrendRecipeByArticle(articleId: string): Promise<TrendRecipe | null> {
  try {
    const { data } = await axiosInstance.get(`${BASE_URL}/recipes/by-article/${articleId}`);
    return data || null;
  } catch {
    return null;
  }
}

export async function generateTrendRecipeForArticle(articleId: string): Promise<TrendRecipe> {
  const { data } = await axiosInstance.post(`${BASE_URL}/recipes/generate/${articleId}`);
  return data;
}
